/* Walkable Store - immersive first-person storefront engine.
 *
 * Architecture (2D now, 3D-ready): the experience is split into four layers
 * that a future WebGL renderer can reuse unchanged -
 *   SceneGraph : pure data - scenes + hotspots in normalized (0..1) space.
 *   Store      : reactive app state (current scene, focus, cart, browse board).
 *   Camera     : which scene is shown + transition intent (emits events).
 *   Renderer2D : the ONLY layer that touches the DOM. Swap for a Renderer3D
 *                consuming the same SceneGraph + Store + Camera to go 3D.
 * Experience wires pointer/keyboard input to declarative hotspot actions.
 */
(function () {
  "use strict";

  var DATA = window.WALKABLE_DATA || {};
  var ASSETS = DATA.assets || {};
  // BW-429: scene art is requested at a CDN-resized width (same file, `&width=W`
  // on the asset_url), never a new filename - the Composer binds the names.
  // Wide art is sharper from WIDE_BP up, decided once at load (never on
  // resize). The section's preload hrefs use the same breakpoint and widths;
  // check_art_pins.py keeps the two in step. ART_ASPECT is the masters' pixel
  // size, used only as an aspect ratio to lay out before the real image arrives.
  var WIDE_BP = "(min-width: 1024px)";
  var ART_W = { wide: window.matchMedia(WIDE_BP).matches ? 2400 : 1600, portrait: 900 };
  var ART_ASPECT = { wide: { w: 6336, h: 2688 }, portrait: { w: 3072, h: 5504 } };
  function sizedArt(url, variant) {
    return url + (url.indexOf("?") < 0 ? "?" : "&") + "width=" + ART_W[variant];
  }
  var COLLECTIONS = DATA.collections || [];
  var FEATURED = DATA.featured || [];
  var SITE = DATA.site || {};              // name, tagline, phone, email, account
  var PAGES = DATA.pages || [];            // the hanging nav-sign planks (pages)
  var PAGE_CONTENT = DATA.pageContent || {}; // handle -> {title, body} for the book
  var WIDE_SWIPE = DATA.wideSwipe || {};   // BW-309: scene id -> theme-setting bool
  // BW-316: 20-100, one number shared by both scenes (theme-editor range
  // wide_swipe_visible, settings_schema.json) - the % of the wide scene's
  // WIDTH visible on screen. wideScale() below turns this into the actual
  // scale factor, capped so the image is never taller than the frame.
  // Defaults to 35 (the schema default, BW-316 step 9) so a harness or a
  // theme build from before this setting existed behaves the same as a
  // fresh theme install.
  var WIDE_SWIPE_VISIBLE = (typeof WIDE_SWIPE.visible === "number" && WIDE_SWIPE.visible > 0) ? WIDE_SWIPE.visible : 35;

  // BW-316: shared by fitFrameHeight() (boxHeight = Hnat - beam) and
  // layoutPan() (boxHeight = the box's actual current height) so the two
  // can never compute a different scale for the same box. scaleFit is the
  // height-fit (no vertical crop, no band); scaleWant is what would show
  // WIDE_SWIPE_VISIBLE% of the image's width. min() caps zooming IN: above
  // scaleFit the image would be taller than the frame with no way to pan
  // down to its bottom, so the height term wins for any setting at or below
  // the frame's own natural width share, and that case is byte-identical to
  // the old zoom-100 behaviour.
  function wideScale(dims, boxWidth, boxHeight) {
    var visible = WIDE_SWIPE_VISIBLE / 100;
    var scaleFit = boxHeight / dims.h;
    var scaleWant = boxWidth / (visible * dims.w);
    return Math.min(scaleFit, scaleWant);
  }

  // /pages/<handle> -> "<handle>" (null for non-page urls like /collections/all)
  function handleFromUrl(url) {
    var m = /\/pages\/([^\/?#]+)/.exec(url || "");
    return m ? m[1] : null;
  }

  // The Storefront plank points at the shop root; treat that as "step outside"
  // (a scene change) rather than a page load / book transition.
  function isHomeUrl(url) {
    if (!url) return false;
    var home = SITE.homeUrl || "/";
    return url === home || url === "/" || url === home + "/" || url + "/" === home;
  }

  // Vertical bands (fraction of the nav-sign image height) of each carved plank
  // in assets/nav-sign-2.png, top plank first. Clickable rows are laid over these.
  // Retune here if the sign art is regenerated with a different plank layout.
  var NAV_PLANKS = [0.1925, 0.3783, 0.5643, 0.7495, 0.9344];

  // Shelf plaques (BW-109, 30-shelf pass 2026-08-20): fixed physical shelf
  // positions on the Long Room's timber beams, measured against Adam's
  // definitive numbered layout (tools/reference/scene-layout.png, 30
  // shelves numbered directly on scene-store-wide-5.jpg's own pixels)
  // and re-derived from the printed label positions - see
  // tools/build_shelf_slots.py. Ids are "shelf-<n>", matching Adam's
  // numbering exactly (clearer for the merchant than the old zone names
  // left-1/backwall-1/etc, which this table replaces wholesale). Zones
  // (Adam's words): 1-7 left aisle = popular fiction; 8-14 right aisle =
  // popular non-fiction; 15-18 back wall = Staff Favourites/Sale/Sale/New
  // Arrivals; 19-22 balcony wall = reference/religion/education/textbooks;
  // 23-26 left side wall = family & children/history; 27-30 right side
  // wall (by the staircase) = home & cooking/gardening/crafts.
  //
  // Each slot's `rect` is an image-space [x,y,w,h] fraction of the WIDE
  // frame; `rectPortrait` is the same for the portrait frame where that
  // shelf is actually visible there - portrait is a SEPARATE composition
  // or a crop of wide, so a slot with no rectPortrait simply has no anchor
  // (and renders no plaque) on that frame, same rule as scene hotspots.
  // Portrait v5 is a separate mobile-native composition. Its confident
  // visible plaque subset is measured independently; a slot with no
  // rectPortrait is intentionally non-interactive on that frame. This
  // table is ONLY geometry - which collection (or bare label, for the
  // not-yet-existing 15-18) lands on which slot is the merchant's
  // shelf_plaque blocks in sections/walkable-store.liquid, never this file.
  //
  // `mount` is a bake-time concept only now (BW-109 evening pivot: signs
  // are baked into the scene JPEG, walkable-2.css/js render nothing
  // visible for them - see assets/ART.md's "Shelf signs"): "standing" bakes
  // the plank flush on a real shelf-top (the 14 aisle-shelf endcaps);
  // "hanging" bakes it below two chains off a wall bookcase's top rail
  // (shelves 15-30, none of which have a flat top to stand on). `tilt`
  // (degrees, STANDING slots only) is baked into the sprite's own pixels
  // by tools/gen_plaques.py to match that shelf's recession angle in THIS
  // art; hanging slots carry no tilt - a chain-hung sign hangs straight
  // down under gravity regardless of the wall's angle.
  //
  // v5 geometry comes directly from tools/bake_scene_labels_v5.py and
  // tools/reference/shelf_signs_manifest_v5.json. The old sprite mount/tilt
  // geometry is not reusable for these freshly composed rooms.
  var SHELF_SLOTS = {
    "shelf-1": { rect: [0.27620, 0.48661, 0.09233, 0.04018], rectPortrait: [0.12695, 0.52289, 0.18555, 0.02671] },
    "shelf-2": { rect: [0.27620, 0.43824, 0.09391, 0.03274], rectPortrait: [0.23926, 0.49873, 0.13444, 0.02725] },
    "shelf-3": { rect: [0.29908, 0.40625, 0.08002, 0.02902], rectPortrait: [0.29460, 0.49273, 0.11230, 0.01744] },
    "shelf-4": { rect: [0.32197, 0.38318, 0.06471, 0.02716] },
    "shelf-5": { rect: [0.33586, 0.36384, 0.06897, 0.02753] },
    "shelf-6": { rect: [0.34643, 0.35119, 0.05319, 0.02158] },
    "shelf-8": { rect: [0.58949, 0.49479, 0.14362, 0.03274], rectPortrait: [0.62988, 0.50327, 0.14648, 0.02271] },
    "shelf-9": { rect: [0.60133, 0.43601, 0.09233, 0.02827], rectPortrait: [0.59733, 0.49146, 0.07650, 0.01999] },
    "shelf-10": { rect: [0.59186, 0.40848, 0.09075, 0.02679] },
    "shelf-11": { rect: [0.57844, 0.37872, 0.07576, 0.02381] },
    "shelf-12": { rect: [0.57607, 0.35640, 0.06155, 0.02158] },
    "shelf-13": { rect: [0.37484, 0.27455, 0.04182, 0.01860] },
    "shelf-14": { rect: [0.42771, 0.27455, 0.04182, 0.01860] },
    "shelf-18": { rect: [0.53267, 0.27455, 0.04182, 0.01860] },
    "shelf-23": { rect: [0.02367, 0.33296, 0.09864, 0.07812], rectPortrait: [0.15625, 0.44331, 0.02930, 0.01672] },
    "shelf-24": { rect: [0.14047, 0.31436, 0.05919, 0.05394], rectPortrait: [0.25228, 0.45331, 0.02441, 0.01508] },
    "shelf-25": { rect: [0.21307, 0.30134, 0.03867, 0.04464], rectPortrait: [0.33366, 0.45694, 0.08138, 0.00999] },
    "shelf-26": { rect: [0.66130, 0.27827, 0.01342, 0.03311], rectPortrait: [0.59082, 0.45694, 0.07975, 0.00999] },
    "shelf-27": { rect: [0.67787, 0.28274, 0.02289, 0.03609], rectPortrait: [0.70475, 0.44876, 0.02767, 0.01817] },
    "shelf-28": { rect: [0.70628, 0.29018, 0.02920, 0.03720], rectPortrait: [0.73893, 0.44241, 0.03255, 0.02362] },
    "shelf-29": { rect: [0.74495, 0.29948, 0.04104, 0.03981], rectPortrait: [0.78125, 0.43695, 0.03743, 0.02725] },
    "shelf-30": { rect: [0.79861, 0.32068, 0.05919, 0.05134], rectPortrait: [0.82194, 0.43150, 0.03743, 0.02998] }
  };

  // The hub every interior scene hangs off. Trail steps are {label, scene,
  // browse?, genre?}: walking to one re-opens the wall board / genre column it
  // names, so a step back lands where you actually were, not just in the room.
  var ROOM = { label: "The Book Store", scene: "longroom" };

  // -------------------------------------------------------------- utilities
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }
  function money(cents) {
    if (cents == null) return "";
    var sym = DATA.currencySymbol || "$";
    return sym + (cents / 100).toFixed(2);
  }
  // A handle names a genre or one of its sub-genres. BW-166: neither carries
  // its own covers any more (the shelf now fetches them - see renderShelf());
  // this just resolves title/count/url for the assistant's "take me to
  // <genre>" path.
  function genreByHandle(handle) {
    var hit = null;
    COLLECTIONS.forEach(function (c) {
      if (hit) return;
      if (c.handle === handle) hit = c;
      else hit = (c.subs || []).find(function (s) { return s.handle === handle; }) || null;
    });
    return hit;
  }
  // BW-163/BW-166: a plaque's action.url is always a real Shopify collection
  // link - /collections/<handle> for a single-target plaque, or
  // /collections/all?filter...(repeated) for a multi-target union (see
  // backend url_for()). Every one of those is a real collection page the
  // shelf can fetch and shelve in-scene (actions.openCollection() below), so
  // the gate is just "is this a /collections/ URL" - it no longer needs to
  // recognise the handle as one of this room's own genres. A non-collection
  // href (a page, /collections with no handle, etc.) returns null and falls
  // through to real navigation.
  function collectionForNavUrl(url) {
    if (!url) return null;
    return /^\/collections\/[^\/?#]+/.test(url.split(/[?#]/)[0]) ? url : null;
  }

  // ---------------------------------------------------------- SceneGraph
  // Hotspots use normalized rects [x, y, w, h] as fractions of the SOURCE ART
  // IMAGE (0..1), not the frame - the art renders with `background-size:
  // cover`, which centre-crops it to the frame's aspect ratio, so a
  // frame-fraction rect drifts off its object whenever that crop shifts
  // (BW-112). `layoutHotspots()` below maps these through the same cover
  // transform on every render and resize. `rectPortrait` is optional and used
  // only when the scene's 9:16 portrait art is the active frame (falls back
  // to `rect` if absent).

  // BW-135: click areas the Scene Composer publishes as theme data, loaded by
  // sections/walkable-store.liquid BEFORE this script (document order). schema
  // 1 or 2 - an unrecognised or absent manifest degrades to no published
  // hotspots for that scene, never an error.
  //
  // BW-141: schema 2 adds `quad` (the four drawn points) alongside `rect`
  // (its bbox) on each entry. `rect` still positions the anchor exactly as
  // schema 1 did; `quad`, when present, additionally clips the anchor's hit
  // area to the true polygon (renderHotspots() below, walkable-2.css
  // .ws-hotpoly) so a click just outside a skewed plaque's drawn shape but
  // inside its bbox misses, matching what's visually there.
  //
  // BW-200: a second Scene Composer project (the storefront) means a second
  // manifest can load, so a single window.WS_SCENE_HOTSPOTS global can't hold
  // both any more. New manifests push onto window.WS_SCENE_MANIFESTS instead;
  // this builds a scene-key -> manifest registry from that array PLUS
  // window.WS_SCENE_HOTSPOTS when set. The old global is a PERMANENT
  // fallback, not a migration step - the already-published long-room asset
  // sets only it and is never rewritten, and a Composer rollback can
  // legitimately re-upload that old-format asset at any point in the future.
  // Two manifests for the same scene (only possible via a hand-pushed stale
  // asset alongside a published one) resolve last-wins with a console.warn.
  //
  // BW-238: the schema test is deliberately open-ended (any number >= 1)
  // rather than an exact 1-or-2 match. A schema-3 manifest (adds a top-level
  // `placeholders` key) published against an exact-match test would be
  // DROPPED WHOLE by this filter - taking that scene's hotspots down with it,
  // not just losing the placeholder - so this relaxation must land before any
  // schema-3 manifest is published (planning/BW-238-SCENE-PLACEHOLDER.md
  // section 2). Entry-level reads are already defensive (`if (!h || !h.id)
  // return;` in publishedFor below), so an unrecognised future schema
  // degrades gracefully rather than throwing.
  var MANIFESTS = {};                       // manifest scene key -> manifest
  (function () {
    var list = (window.WS_SCENE_MANIFESTS || []).slice();
    if (window.WS_SCENE_HOTSPOTS) list.push(window.WS_SCENE_HOTSPOTS);
    list.forEach(function (m) {
      if (!m || typeof m.schema !== "number" || m.schema < 1 || !m.scene) return;
      if (MANIFESTS[m.scene]) console.warn('BW-200: two manifests for scene "' + m.scene + '"; last wins');
      MANIFESTS[m.scene] = m;
    });
  })();
  var SCENE_MANIFEST_KEY = { longroom: "long-room", storefront: "storefront" };

  // BW-238: folds each published manifest's optional `placeholders`
  // ({wide, portrait} data URIs from services.scenes.placeholder) into a
  // scene-id-keyed registry, the same way MANIFESTS folds hotspots. A
  // manifest with no `placeholders` key - rendered before this change, or a
  // scene with no hotspot layers (BW-238's stated limitation, since only a
  // revision with hotspot layers gets a manifest at all) - simply has no
  // entry here, so Renderer2D.applySceneArt's PLACEHOLDERS[id] lookup comes
  // back undefined and the placeholder path is skipped for that scene,
  // exactly like today's no-manifest behaviour.
  var PLACEHOLDERS = {};                    // scene id -> {wide, portrait}
  Object.keys(SCENE_MANIFEST_KEY).forEach(function (sceneId) {
    var manifest = MANIFESTS[SCENE_MANIFEST_KEY[sceneId]];
    if (manifest && manifest.placeholders) PLACEHOLDERS[sceneId] = manifest.placeholders;
  });

  // Folds the flat published manifest (one row per {id, variant}) into the
  // hotspot def shape layoutHotspots()/renderHotspots() already understand,
  // pairing a wide and portrait row for the same id. `strictFrame: true`
  // marks a published def so layoutHotspots() hides it outright on a frame
  // it has no rect for, rather than falling back to its wide rect (the
  // structural hotspots' existing behaviour, which stays unchanged).
  // `existingIds` are ids already claimed (the scene's structural hotspots);
  // a collision is dropped with one console.warn rather than silently
  // overwriting or duplicating a DOM node - published ids are namespaced
  // "pub-" so this should never actually fire, but the merge is defensive.
  //
  // BW-200: an entry carrying `scene` is a "walk to another scene" click area
  // (e.g. the storefront door) rather than a link to a collection page, and
  // maps to a `goto` action - but keeps its real `url` too (runAction()
  // below falls back to it if the target scene ever drifts out of the
  // graph), so a modified click, a failed in-world walk, or a JS-off visitor
  // still gets a working link.
  function publishedFor(sceneId, existingIds) {
    var manifest = MANIFESTS[SCENE_MANIFEST_KEY[sceneId]];
    var published = (manifest && manifest.hotspots) || [];
    if (!published.length) return [];
    var claimed = {};
    (existingIds || []).forEach(function (id) { claimed[id] = true; });
    var byId = {};
    var order = [];
    published.forEach(function (h) {
      if (!h || !h.id) return;
      if (claimed[h.id]) {
        if (!byId[h.id]) console.warn('BW-135: published hotspot id "' + h.id + '" collides with an existing hotspot; dropped');
        return;
      }
      if (!byId[h.id]) {
        byId[h.id] = {
          id: h.id,
          strictFrame: true,
          label: h.label,
          // BW-305: whitelisted rather than passed through, so an unknown
          // future value degrades to "hover" (no idle affordance, today's
          // behaviour) instead of emitting a junk CSS class.
          presentation: (h.presentation === "pulse" || h.presentation === "outline") ? h.presentation : "hover",
          action: h.scene
            ? { type: "goto", scene: h.scene, forward: h.forward !== false, url: h.url }
            : { type: "nav", url: h.url }
        };
        order.push(h.id);
      }
      if (h.variant === "portrait") {
        byId[h.id].rectPortrait = h.rect;
        if (h.quad) byId[h.id].quadPortrait = h.quad;
      } else {
        byId[h.id].rect = h.rect;
        if (h.quad) byId[h.id].quad = h.quad;
      }
    });
    return order.map(function (id) { return byId[id]; });
  }

  // BW-139: true once the manifest supplies click areas for a scene - the
  // Composer then owns that scene's links entirely, so the older BW-109
  // shelf_plaque anchors (shelfmarks) must not render alongside them: they
  // sit at the same screen positions and, being on top, would win every
  // mouse click over the (possibly re-pointed) published hotspot underneath.
  // An empty/absent manifest - the r0 bootstrap, or any scene
  // SCENE_MANIFEST_KEY doesn't map - falls back to shelfmarks unchanged, so
  // rolling a scene back to r0 keeps the plaques clickable.
  function scenePublished(sceneId) {
    var manifest = MANIFESTS[SCENE_MANIFEST_KEY[sceneId]];
    return !!(manifest && manifest.hotspots && manifest.hotspots.length);
  }

  // BW-200: "the scene's way forward" (the mobile primary-action button and
  // its `actions.primary()` handler) is the first `goto` hotspot on the
  // scene - an in-world camera move. Before BW-200 a published hotspot was
  // always `action.type === "nav"` (a link to a collection page), so this
  // never had to distinguish one from a walk; a published hotspot can now
  // ALSO be `goto` (a Composer-authored "walk to a scene" click area, e.g.
  // the storefront door), and picking that up here is exactly right - a
  // shopper tapping "forward" should walk through the door, same as tapping
  // it directly. The Long Room's own structural "walk down the aisle" goto
  // hotspot was removed in BW-143, so this still returns null there (and for
  // the shelf scene, which never had one) unless a scene walk is published.
  function forwardHotspot(scene) {
    var list = (scene && scene.hotspots) || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].action && list[i].action.type === "goto") return list[i];
    }
    return null;
  }

  // BW-200: the hand-authored structural door hotspot, kept as the fallback
  // for as long as the storefront project has no published manifest (the r0
  // bootstrap, or a rollback to it) - see the D5 conditional in
  // buildSceneGraph() below. Once the storefront publishes any hotspots it
  // owns the door click area entirely, same as BW-139's shelfmark occlusion
  // for the Long Room.
  var STOREFRONT_DOOR = {
    id: "door", rect: [0.40, 0.36, 0.20, 0.55],
    rectPortrait: [0.36, 0.42, 0.28, 0.50],
    label: "Push the door open",
    action: { type: "goto", scene: "longroom", forward: true }
  };

  function buildSceneGraph() {
    var fiction = COLLECTIONS.filter(function (c) { return c.group === "fiction"; });
    var nonfiction = COLLECTIONS.filter(function (c) { return c.group !== "fiction"; });

    return {
      order: ["storefront", "longroom", "shelf"],
      scenes: {
        storefront: {
          id: "storefront",
          name: "Storefront",
          art: sizedArt(ASSETS.storefrontWide, "wide"),
          artPortrait: sizedArt(ASSETS.storefrontPortrait, "portrait"),
          fit: "cover",
          location: { title: "HIGH STREET", sub: "Bookworms · Overcast 3:40pm" },
          beam: true,       // the MENU nav rides the beam out here too
          navSign: true,    // so you can jump to any page from the street
          assistant: "mini", // BW-352: Adam's call - she is out front too, not
          // just once you are through the door. Same corner bust everywhere;
          // the mobile "Go through the door" CTA (.ws-mact) always shows here
          // (the STOREFRONT_DOOR fallback is a `goto` hotspot), so her mobile
          // in-frame clearance is raised further whenever that button is
          // shown too (root.classList "has-forward", assistant.css).
          hotspots: scenePublished("storefront") ? publishedFor("storefront") : [STOREFRONT_DOOR]
        },
        longroom: {
          id: "longroom",
          name: "The Book Store",
          // 21:9 art, so the beam-shortened box is never wider than the picture
          // and `cover` only ever trims the side walls - the mezzanine above and
          // the entrance stairs below both survive. See tools/gen_scenes.py.
          art: sizedArt(ASSETS.storeWide, "wide"),
          artPortrait: sizedArt(ASSETS.storePortrait, "portrait"),
          fit: "cover",
          location: { title: "THE BOOK STORE", sub: "Ground floor · gallery above" },
          // BW-115: the wall drawers are gone (Concept 1, BW-113) - Browse
          // now lives on the beam.
          // in-world UI props unique to this scene (rendered by the overlay):
          beam: true,       // top wooden beam: name, phone, email, account, bag
          navSign: true,    // hanging page-nav sign on the left
          assistant: "mini", // BW-114 rework: corner bust, same as the shelf
          // BW-143: the hand-authored structural "Walk down the aisle" rug
          // hotspot (id "walkin") was removed 2026-08-26 - Browse (on the
          // beam) already covers genre navigation, and the extra rug click
          // area was just confusing alongside it. The Long Room's hotspots
          // are now entirely the Composer-published click areas (BW-135), in
          // manifest order; an empty/unpublished manifest means no hotspots
          // at all here, same as the shelf scene below.
          hotspots: publishedFor("longroom")
        },
        // The shelf is a place you walk INTO, so it owns no back hotspot: the
        // hanging trail sign is the way out. (The old invisible rect was
        // unclickable anyway - .ws-shelfwrap covers the whole frame.)
        shelf: {
          id: "shelf",
          name: "The Shelves",
          // No background art (BW-162, owner's call - was scene-shelf.webp):
          // the opaque .ws-shelfwrap/.ws-shelfscroll bookcase grid
          // (assets/shelf.css) sits above this scene at z-index 9 vs the
          // scenes layer's z-index 1, so the photo was always fully hidden
          // the moment the shelf grid painted. applySceneArt() already no-ops
          // on a scene with no `art`, so this scene simply renders its
          // --frame-bg colour behind the grid instead of a photo no one saw.
          fit: "cover",
          location: { title: "AISLE 1 · FICTION", sub: "Covers face out" },
          assistant: "mini"  // she follows you down, as a portrait in the corner
        }
      },
      fiction: fiction,
      nonfiction: nonfiction
    };
  }

  // -------------------------------------------------------------- Store
  function createStore(initial) {
    var state = initial;
    var subs = [];
    return {
      get: function () { return state; },
      set: function (patch) {
        state = Object.assign({}, state, patch);
        subs.forEach(function (fn) { fn(state); });
      },
      subscribe: function (fn) { subs.push(fn); return state; }
    };
  }

  // -------------------------------------------------------------- Camera
  // Walking somewhere always resets what you were holding: the focus, the wall
  // boards, and the trail (the new scene supplies its own, or none).
  function createCamera(graph, store) {
    return {
      goTo: function (sceneId, opts) {
        opts = opts || {};
        var cur = store.get().scene;
        if (cur === sceneId) return;
        store.set({
          prevScene: cur,
          scene: sceneId,
          forward: opts.forward !== false,
          focus: null,
          browseOpen: false,
          browseGenre: null,
          trail: opts.trail || []
        });
      }
    };
  }

  // BW-155: touch two-tap for click areas. Hover has no touch equivalent, so
  // on a coarse-pointer device a bare tap must not fire the area straight
  // through - it only "arms" it (renderHotspots() below). A plain `click`
  // event is always a MouseEvent, never a PointerEvent, so it carries no
  // pointerType of its own; track the pointerdown that precedes every click
  // instead. matchMedia('(hover:none)') alone would also two-tap a
  // touchscreen laptop's mouse clicks, which must keep single-click/hover.
  var lastPointerType = null;
  document.addEventListener("pointerdown", function (ev) {
    lastPointerType = ev.pointerType;
  });
  // Keyboard-triggered clicks (Enter/Space on a focused control) are
  // synthetic MouseEvents with detail:0, unlike a real tap or click
  // (detail:1) - excluding them keeps focus+Enter behaving exactly as
  // before even right after a touch interaction elsewhere on the page.
  function isTouchClick(ev) {
    return lastPointerType === "touch" && ev.detail !== 0;
  }

  // -------------------------------------------------------------- Renderer2D
  function Renderer2D(root, graph, store, camera, actions) {
    var dom = {};
    // BW-380: the page-book's open handle (null when no book is open). Book
    // state lives outside the store (it is DOM-local, not reactive app
    // state), so the URL/history sync below tracks it here directly rather
    // than through store.subscribe.
    var openBookHandle = null;
    // BW-309 step 6h: root's own direct parent, `#walkable-store.ws-stage`
    // (see createFrame() at the bottom of this file - .ws-frame is always
    // appended straight into host with no wrapper between them). This is
    // the element BW-99's 100dvh rule actually sits on; fitFrameHeight()
    // below overrides it inline in wide-swipe mode only.
    var stage = root.parentElement;
    // BW-155: the one click area currently armed by a first touch tap, and
    // its auto-disarm timer. Module-scoped (not per-hotspot) because only
    // one area is ever armed at a time.
    var armedHotspot = null;
    var armedTimer = null;
    var HOTSPOT_ARM_MS = 4000;
    function disarmHotspot() {
      clearTimeout(armedTimer);
      armedTimer = null;
      if (armedHotspot) armedHotspot.classList.remove("is-armed");
      armedHotspot = null;
    }
    function armHotspot(elm) {
      disarmHotspot();
      elm.classList.add("is-armed");
      armedHotspot = elm;
      armedTimer = setTimeout(disarmHotspot, HOTSPOT_ARM_MS);
    }

    // BW-238: IMG_DIMS/ART_READY must exist before build() runs, not lower
    // down where the image-space anchoring code (loadImageDims, below) used
    // to declare IMG_DIMS alone - whenArtReady's im.complete fast path can
    // resolve SYNCHRONOUSLY, same tick, for a scene's art that the page's
    // own <link rel=preload> already finished fetching by the time this
    // deferred script runs, and that fast path can fire during the very
    // first build() -> applySceneArt() call below.
    var IMG_DIMS = {};   // asset url -> {w, h}, once the image has loaded
    var ART_READY = {};  // art url -> true, once loaded AND decoded
    function whenArtReady(url, cb) {
      if (!url) return;
      if (ART_READY[url]) { cb(); return; }
      var im = new Image(), fired = false;
      function done() {
        if (fired) return; fired = true;
        ART_READY[url] = true;
        var seeded = IMG_DIMS[url], real = { w: im.naturalWidth, h: im.naturalHeight };
        if (!seeded) IMG_DIMS[url] = real;
        else if (Math.abs(seeded.w / seeded.h / (real.w / real.h) - 1) > 0.01) {
          // BW-429: the master's aspect differs from the seed (new art or a
          // rollback) - trust the real image and re-lay out the current scene.
          IMG_DIMS[url] = real;
          requestAnimationFrame(relayoutCurrentScene);
        }
        cb();
      }
      im.onload = function () { im.decode ? im.decode().then(done, done) : done(); };
      im.onerror = function () {};                   // leave the placeholder up
      im.src = url;
      if (im.complete && im.naturalWidth) done();    // memory-cache fast path, same tick
    }

    // BW-429: seed every scene's art dimensions so hotspots lay out on first
    // paint; layout only uses the aspect ratio, and whenArtReady keeps the seed.
    graph.order.forEach(function (id) {
      var s = graph.scenes[id];
      if (s.art) IMG_DIMS[s.art] = ART_ASPECT.wide;
      if (s.artPortrait) IMG_DIMS[s.artPortrait] = ART_ASPECT.portrait;
    });

    function relayoutCurrentScene() {
      var scene = graph.scenes[store.get().scene];
      fitFrameHeight(scene, wideSwipeActive(scene.id));
      layoutHotspots(scene);
      layoutPlaques();
    }

    build();

    // BW-155: tapping anywhere outside the armed click area disarms it -
    // the scene itself, another hotspot (its own tap already re-arms via
    // armHotspot()'s disarmHotspot() call, so this is a harmless no-op
    // there), or any other in-world control.
    document.addEventListener("click", function (ev) {
      if (armedHotspot && !ev.target.closest(".ws-hotspot")) disarmHotspot();
    });

    // BW-309: swipe-to-pan gesture. One pointerdown handler on root; the
    // frame's own `touch-action: pan-y` (walkable-2.css, gated
    // .is-wideswipe) hands us horizontal movement and leaves vertical page
    // scroll native. A drag starting ON a hotspot is allowed and must pan -
    // ".ws-hotspot" is deliberately absent from the chrome-exclusion list
    // below.
    var panDrag = null;    // in-flight drag state, or null between gestures
    var dragEndedAt = 0;   // BW-155-style: suppresses the click a pan leaves behind
    var PAN_LOCK_PX = 8;
    var PAN_FLICK_PX_MS = 0.5;
    var PAN_RUBBER = 0.35;

    // BW-309 step 6e: seven evenly-spaced rests, f_i = i/6 for i in 0..6,
    // replacing the original three ({0, 0.5, 1}). REST_COUNT is the single
    // source of truth - everything that used to hardcode [0, 0.5, 1] or a
    // flat +/-0.5 step now derives from RESTS/REST_COUNT instead. The odd
    // count keeps f=0.5 (index 3) an exact rest, so scene entry still lands
    // dead-centre.
    var REST_COUNT = 7;
    var RESTS = [];
    for (var _ri = 0; _ri < REST_COUNT; _ri++) RESTS.push(_ri / (REST_COUNT - 1));

    function writePanX(sc, panX) {
      sc.style.setProperty("--ws-panx", panX + "px");
      dom.hotspots.style.setProperty("--ws-panx", panX + "px");
    }

    // BW-309: the one-time peek - a small, one-shot ease left and back on
    // the first wide-swipe scene a session actually lands on, so a shopper
    // who never touches the picture still learns it pans. Once per
    // sessionStorage-backed session (a private window or blocked storage
    // fails "done" so it just never peeks, rather than replaying every
    // reload); sessionStorage access is wrapped since it can throw.
    var PEEK_KEY = "bw:ws-peek";
    var peekDone = true;
    try { peekDone = sessionStorage.getItem(PEEK_KEY) === "1"; } catch (e) { peekDone = true; }
    function markPeekDone() {
      peekDone = true;
      try { sessionStorage.setItem(PEEK_KEY, "1"); } catch (e) {}
    }

    // BW-309 step 6d: set to the active peek's own cancel function once its
    // transition genuinely starts (runPeek() below), cleared back to null
    // once it ends - lets focus-follows-pan and the arrow keys cancel an
    // in-flight peek (shopper intent wins) without reaching into runPeek's
    // own closure. A bare pointerdown-anywhere listener (already inside
    // runPeek) covers the touch/mouse case; keyboard interaction needs its
    // own hook since neither of these is a pointerdown.
    var cancelActivePeek = null;

    // Called ONLY from maybeAttemptPeekOnEntry() below, which is itself
    // only called from render()'s scene-changed block, AFTER that same
    // render() pass has already run layoutHotspots()/layoutPan() for the
    // entering scene (BW-309 step 6b2 - see that call site's comment for
    // why the ordering matters: layoutPan() unconditionally re-derives
    // --ws-panx from PAN_F on every layout pass, and calling this any
    // earlier in the same render() tick let that later, synchronous
    // layoutPan() call silently overwrite the peek's own write before the
    // browser ever painted it - found live by the tester, a real NO-GO).
    function maybeStartPeek(id, sc) {
      if (peekDone) return;
      // build()'s own initial applySceneArt(store.get().scene) call (above,
      // before dom.hotspots exists yet) can reach this synchronously, on
      // the very first scene, via ART_READY's memory-cache fast path -
      // defer one tick rather than throw on dom.hotspots.getBoundingClientRect()
      // below; by the next tick build() (and the immediate first render())
      // have both finished, so a real check runs against the settled DOM.
      if (!dom.hotspots) { setTimeout(function () { maybeStartPeek(id, sc); }, 0); return; }
      if (id !== store.get().scene || !wideSwipeActive(id)) return;
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        markPeekDone();
        return;
      }
      var dims = IMG_DIMS[graph.scenes[id].art];
      // BW-309 step 6g4/6i: box from dom.scenes (the never-inline-
      // stretched .ws-scenes wrapper), not dom.hotspots' own rect - see
      // layoutPan()'s own comment for why (both left/right AND top/height
      // are stretched on dom.hotspots now, so its own rect is post-stretch
      // in both dimensions).
      var box = dom.scenes.getBoundingClientRect();
      if (!dims || !box.height) return;
      // BW-316: wideScale() must match layoutPan()'s own scale exactly, or
      // this overflow disagrees with the one the peek is about to animate
      // against.
      var overflow = Math.max(0, dims.w * wideScale(dims, box.width, box.height) - box.width);
      if (overflow < 24) { markPeekDone(); return; }   // same floor as layoutPan(), plus headroom
      markPeekDone();
      runPeek(sc, overflow, id);
    }

    // BW-309 step 6b: called from render() on every scene ENTRY (strictly
    // after that same render() pass's own layoutHotspots()/layoutPan() call
    // for the entering scene - see the render() call site's own comment)
    // - the real, reliable trigger for the peek, independent of
    // applySceneArt's no-op-on-unchanged-url guard. Root cause of the peek
    // never firing on a real page load, confirmed live in the harness:
    // prefetchScenes() (once the active scene's art is ready and the window
    // has loaded, then idle) applies the Long Room's art in the
    // background while the shopper is still looking at the storefront -
    // applySceneArt() used to call maybeStartPeek() at that moment, which
    // correctly declined (store.get().scene isn't "longroom" yet), but
    // applySceneArt() only ever reached that call from inside its own
    // "first time this scene's art URL is being applied" branch - once the
    // prefetch had set sc.dataset.art, every later applySceneArt("longroom")
    // call (including the real one from render() when the shopper actually
    // walks in) hit its early-return no-op and never got a second chance.
    // applySceneArt() no longer calls maybeStartPeek() at all (step 6b2) -
    // this function is the sole trigger now. It always asks whenArtReady
    // for the scene's own (already-selected) art, which is a synchronous
    // no-op when ART_READY is already true (the prefetched case - attempts
    // immediately on entry) and a real wait otherwise (a cold scene -
    // attempts once its art loads, same as before).
    function maybeAttemptPeekOnEntry(id) {
      if (peekDone || !wideSwipeActive(id)) return;
      var sc = dom.scenes.querySelector('[data-scene="' + id + '"]');
      if (!sc) return;
      whenArtReady(graph.scenes[id].art, function () { maybeStartPeek(id, sc); });
    }

    // Ease --ws-panx to a small negative offset over 480ms, hold 160ms,
    // ease back over 620ms - a direct inline `transition`, not the
    // is-panning/is-snapping classes (their durations don't match this
    // profile), cleared back to unset once done so the base CSS rules
    // resume normal control. Cancelled immediately by ANY pointerdown
    // anywhere (not just one that would start a real pan) - the shopper's
    // own gesture always wins, so this listens on document in the capture
    // phase rather than folding into the root pointerdown handler above.
    //
    // BW-309 step 6b2: the caller (maybeStartPeek(), via
    // maybeAttemptPeekOnEntry()) already runs strictly after this render()
    // pass's own layoutPan() has written the scene's rest --ws-panx with no
    // transition - but that write has not necessarily been PAINTED yet
    // (still the same synchronous tick, or the very next one). Setting a
    // transition and changing --ws-panx again before the rest value has
    // ever reached the screen collapses the whole "0px -> -56px" move into
    // the first frame the browser paints, with no earlier state to animate
    // from - invisible, same failure mode as the ordering bug, just one
    // layer further in. Two nested requestAnimationFrame calls guarantee at
    // least one real painted frame at rest happens first: the first
    // callback runs at the start of the next frame (whatever was already
    // on screen from THIS tick gets painted going into it), the second
    // runs at the start of the frame after that, by which point the rest
    // frame is guaranteed to have been on screen. `.is-peeking` (added only
    // once the transition genuinely starts) is what layoutPan() checks to
    // avoid re-deriving and clobbering --ws-panx while this is running - a
    // resize or the BW-168 has-beam transitionend re-layout during the
    // 480+160+620=1260ms window no longer undoes it mid-flight.
    function runPeek(sc, overflow, id) {
      var amount = -Math.min(56, overflow * 0.18);
      var cleared = false;
      function restPanX() {
        // Matches exactly what layoutPan() would compute for the CURRENT
        // PAN_F, so is-peeking coming off never leaves a visible jump -
        // PAN_F never changes during a peek, so this is always the scene's
        // entry rest (f=0.5) in practice, derived rather than hardcoded.
        var f = PAN_F[id] != null ? PAN_F[id] : 0.5;
        return (0.5 - f) * overflow;
      }
      function clearPeek() {
        if (cleared) return;
        cleared = true;
        sc.classList.remove("is-peeking");
        dom.hotspots.classList.remove("is-peeking");
        sc.style.transition = "";
        dom.hotspots.style.transition = "";
        document.removeEventListener("pointerdown", cancelPeek, true);
        if (cancelActivePeek === cancelPeek) cancelActivePeek = null;
        // BW-309 step 6i: the peek runs during the same ~1-1.5s window the
        // has-beam top transition can still be settling in (found live:
        // the .ws-scenes transitionend fires around t~1s, close enough to
        // the peek's own tail that layoutPan()'s animating guard - is-
        // peeking, correctly - can swallow that re-layout before this
        // scene's stretch/pan ever picks up the settled geometry). One
        // more fresh layout once the peek itself is fully done, whichever
        // way it ended, closes that gap regardless of which finished last.
        var scene = graph.scenes[id];
        if (scene) layoutHotspots(scene);
      }
      function cancelPeek() {
        clearPeek();
        writePanX(sc, restPanX());   // instantly back to rest; the shopper's own drag takes it from here
      }
      document.addEventListener("pointerdown", cancelPeek, true);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (cleared) return;   // a pointerdown already cancelled it during the wait
          cancelActivePeek = cancelPeek;
          sc.classList.add("is-peeking");
          dom.hotspots.classList.add("is-peeking");
          sc.style.transition = "transform 480ms ease";
          dom.hotspots.style.transition = "transform 480ms ease";
          writePanX(sc, amount);
          setTimeout(function () {
            if (cleared) return;
            setTimeout(function () {
              if (cleared) return;
              sc.style.transition = "transform 620ms ease";
              dom.hotspots.style.transition = "transform 620ms ease";
              writePanX(sc, restPanX());
              setTimeout(clearPeek, 640);
            }, 160);
          }, 480);
        });
      });
    }

    // Nearest of the REST_COUNT rests in RESTS to the panX actually on
    // screen, biased by release velocity - a flick over PAN_FLICK_PX_MS
    // moves one rest INDEX further in its direction than the
    // plain-nearest answer (BW-309 step 6e: index-based, not a flat 0.5).
    function nearestStopF(panX, overflow, vx) {
      var fRaw = overflow > 0 ? (0.5 - panX / overflow) : 0.5;
      var nearestIdx = 3, bestDist = Infinity;
      RESTS.forEach(function (f, i) {
        var dist = Math.abs(f - fRaw);
        if (dist < bestDist) { bestDist = dist; nearestIdx = i; }
      });
      if (vx > PAN_FLICK_PX_MS) nearestIdx = Math.max(0, nearestIdx - 1);
      else if (vx < -PAN_FLICK_PX_MS) nearestIdx = Math.min(REST_COUNT - 1, nearestIdx + 1);
      return RESTS[nearestIdx];
    }

    function snapTo(sc, overflow, f) {
      sc.classList.remove("is-panning");
      dom.hotspots.classList.remove("is-panning");
      sc.classList.add("is-snapping");
      dom.hotspots.classList.add("is-snapping");
      writePanX(sc, (0.5 - f) * overflow);
    }

    // transitionend is filtered to "transform" so the pan's own snap
    // animation never gets confused with the has-beam `top` transition
    // (BW-167, the existing dom.hotspots listener a few lines above/below)
    // or the scene crossfade's `opacity` transition.
    dom.scenes.addEventListener("transitionend", function (e) {
      if (e.propertyName !== "transform") return;
      if (e.target.classList && e.target.classList.contains("is-snapping")) {
        e.target.classList.remove("is-snapping");
      }
    });
    dom.hotspots.addEventListener("transitionend", function (e) {
      if (e.target !== dom.hotspots || e.propertyName !== "transform") return;
      dom.hotspots.classList.remove("is-snapping");
    });

    // BW-309 step 6c: --ws-panx's own current value, read straight from the
    // DOM rather than trusting any one panDrag object's own tracked
    // curPanX - the multi-touch bug this guards against left --ws-panx
    // written by a pointer whose panDrag object had already been
    // discarded/overwritten, so a check against that object's own state
    // would have missed it.
    function currentPanX(sc) {
      var n = parseFloat(sc.style.getPropertyValue("--ws-panx"));
      return isNaN(n) ? 0 : n;
    }
    function isAtRest(panX, overflow) {
      var EPS = 0.5; // px
      return RESTS.some(function (f) {
        return Math.abs(panX - (0.5 - f) * overflow) < EPS;
      });
    }

    // Resolves the PRIMARY pointer's gesture on release/cancel/lost-capture.
    // A locked drag always snaps (to the nearest stop on a plain release,
    // back to the pre-drag rest on a cancel/lost-capture). An unlocked
    // drag's own curPanX was never written to by this drag (only a locked
    // drag's pointermove branch calls writePanX) so it is always already a
    // legal rest by construction - the isAtRest check below is a pure no-op
    // for an ordinary tap (no spurious snap class or click suppression) and
    // only ever does real work as a defensive recovery for --ws-panx being
    // left off-rest by something OTHER than this gesture (the multi-touch
    // race fix (1) below closes the one such path found live, but this
    // stays as a second, independent line of defence against any future
    // one - "always resolve to a legal rest" is the actual invariant, not
    // just "undo this one bug").
    function endPanDrag(ev, cancelled) {
      if (!panDrag || ev.pointerId !== panDrag.pointerId) return;
      var d = panDrag;
      panDrag = null;
      try { root.releasePointerCapture(ev.pointerId); } catch (e) {}
      if (d.locked) {
        dragEndedAt = ev.timeStamp;
        var f = cancelled
          ? (PAN_F[d.sceneId] != null ? PAN_F[d.sceneId] : 0.5)
          : nearestStopF(d.curPanX, d.overflow, d.vx);
        PAN_F[d.sceneId] = f;
        snapTo(d.sc, d.overflow, f);
        return;
      }
      var actual = currentPanX(d.sc);
      if (!isAtRest(actual, d.overflow)) {
        dragEndedAt = ev.timeStamp;
        var restF = nearestStopF(actual, d.overflow, 0);
        PAN_F[d.sceneId] = restF;
        snapTo(d.sc, d.overflow, restF);
      }
    }

    // BW-309 step 6g7: .ws-frame is overflow:hidden and never meant to
    // scroll - wide-swipe positioning is done entirely with the
    // --ws-panx transform. But the browser's own native "scroll focused
    // element into view" behaviour (triggered by any .focus(), including
    // real Tab presses) looks at a focused hotspot's untransformed LAYOUT
    // position, which - since .ws-hotspots' children are positioned by
    // percentage against an already-stretched container - regularly sits
    // far outside [0, boxWidth] even when translateX(--ws-panx) has
    // already moved it correctly into view. The browser then sets
    // root.scrollLeft/scrollTop to compensate, uncoordinated with and
    // fighting the transform, visibly misplacing an otherwise-correct
    // rest (found live: three Long Room plates landed fully offscreen
    // after focus-follows-pan chose their correct rest). Fix: this is
    // never a real scroll surface, so any attempt to move it is reset
    // immediately.
    root.addEventListener("scroll", function () {
      if (root.scrollLeft !== 0 || root.scrollTop !== 0) {
        root.scrollLeft = 0;
        root.scrollTop = 0;
      }
    });

    root.addEventListener("pointerdown", function (ev) {
      // BW-309 step 6c: a drag already in flight keeps sole ownership of
      // panDrag - a second (or third...) concurrent pointer is ignored
      // outright rather than reassigning panDrag to it, which used to
      // orphan the primary pointer's own in-progress drag: its later
      // pointermove/pointerup no longer matched panDrag.pointerId, so
      // --ws-panx got stuck wherever the primary pointer had last written
      // it, off every rest, with no snap ever firing. The primary pointer
      // keeps capture and keeps driving the pan; extra touches (a second
      // finger resting on the glass, a genuine two-finger gesture) never
      // take over.
      if (panDrag) return;
      if (ev.target.closest(".ws-beam, .ws-navsign, .ws-mtabs, .ws-mact, .ws-browse, .ws-overlay, .ws-assist, .ws-trailboard")) return;
      var sceneId = store.get().scene;
      if (!wideSwipeActive(sceneId)) return;
      var sc = dom.scenes.querySelector('[data-scene="' + sceneId + '"]');
      if (!sc) return;
      var dims = IMG_DIMS[graph.scenes[sceneId].art];
      // BW-309 step 6g4/6i: box from dom.scenes - see layoutPan()'s own
      // comment; dom.hotspots' own rect is post-stretch in both
      // dimensions now.
      var box = dom.scenes.getBoundingClientRect();
      if (!dims || !box.height) return;
      var overflow = Math.max(0, dims.w * wideScale(dims, box.width, box.height) - box.width);
      if (overflow < 1) return;   // inert - same floor as layoutPan()
      var f0 = PAN_F[sceneId] != null ? PAN_F[sceneId] : 0.5;
      panDrag = {
        sceneId: sceneId,
        pointerId: ev.pointerId,
        sc: sc,
        startX: ev.clientX,
        startY: ev.clientY,
        startPanX: (0.5 - f0) * overflow,
        curPanX: (0.5 - f0) * overflow,
        overflow: overflow,
        locked: false,
        lastX: ev.clientX,
        lastT: ev.timeStamp,
        vx: 0
      };
    });

    root.addEventListener("pointermove", function (ev) {
      if (!panDrag || ev.pointerId !== panDrag.pointerId) return;
      var dx = ev.clientX - panDrag.startX;
      var dy = ev.clientY - panDrag.startY;
      if (!panDrag.locked) {
        if (Math.abs(dx) <= PAN_LOCK_PX || Math.abs(dx) <= Math.abs(dy)) return;
        panDrag.locked = true;
        try { root.setPointerCapture(ev.pointerId); } catch (e) {}
        panDrag.sc.classList.remove("is-snapping");
        panDrag.sc.classList.add("is-panning");
        dom.hotspots.classList.remove("is-snapping");
        dom.hotspots.classList.add("is-panning");
      }
      var dt = ev.timeStamp - panDrag.lastT;
      if (dt > 0) panDrag.vx = (ev.clientX - panDrag.lastX) / dt;
      panDrag.lastX = ev.clientX;
      panDrag.lastT = ev.timeStamp;

      var raw = panDrag.startPanX + dx;
      var half = panDrag.overflow / 2;
      var panX = raw;
      if (raw > half) panX = half + (raw - half) * PAN_RUBBER;
      else if (raw < -half) panX = -half + (raw + half) * PAN_RUBBER;
      panDrag.curPanX = panX;
      writePanX(panDrag.sc, panX);
    });

    root.addEventListener("pointerup", function (ev) { endPanDrag(ev, false); });
    root.addEventListener("pointercancel", function (ev) { endPanDrag(ev, true); });
    // BW-309 step 6c: capture can be force-released by the browser/OS
    // without a pointerup/pointercancel ever following (e.g. an
    // interleaved system gesture) - treat it exactly like a cancel so a
    // drag never gets abandoned mid-gesture with --ws-panx stuck off-rest.
    // endPanDrag() is idempotent against a pointerup that already fired
    // for the same pointer (panDrag is null by then, so this is a no-op).
    //
    // BW-309 step 6f: `lostpointercapture` BUBBLES, and it fires on the
    // OLD capture holder the instant the pointermove lock branch below
    // calls root.setPointerCapture() - a touch pointer is implicitly
    // captured by its hit-test target (e.g. a `.ws-hotspot` under the
    // finger) from pointerdown, so transferring capture to root fires this
    // event FIRST on that child element, which then bubbles through root.
    // The original code reacted to that bubbled event as if root itself
    // had lost capture, cancelling the drag one move after it locked -
    // real on a phone (found live: Adam's swipe stopped tracking after
    // step 6c), invisible in every prior test because those used synthetic
    // PointerEvents dispatched without real implicit capture, so this
    // bubble never happened. Only root LOSING ITS OWN capture (ev.target
    // === root - a genuine externally forced release, or this same
    // drag's own releasePointerCapture() call inside endPanDrag(), which
    // is already a no-op by the time it fires since panDrag is null by
    // then) should read as a cancel.
    root.addEventListener("lostpointercapture", function (ev) {
      if (ev.target !== root) return;
      endPanDrag(ev, true);
    });

    // BW-155-style: a pan must never arm or fire a hotspot. One
    // capture-phase gate covers the hotspot anchor's own click handler, the
    // document-level disarm handler above, and any synthesized click the
    // browser still emits after a touch drag - all of them are bubble-phase
    // listeners, so stopping the event here (capture, before the target is
    // even reached) suppresses every one of them in a single place.
    root.addEventListener("click", function (ev) {
      if (dragEndedAt && (ev.timeStamp - dragEndedAt) < 350) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    }, true);

    // BW-309 step 6d: a local copy of Experience/start()'s own isTyping()
    // (assets/walkable-2.js, the Escape-key handler) - that one lives in a
    // SIBLING function scope (start()), not an enclosing one, so it is not
    // actually reachable from inside Renderer2D (found live: the arrow-key
    // handler below threw "ReferenceError: isTyping is not defined" on
    // every keydown, silently, since a listener's uncaught exception
    // doesn't propagate back through dispatchEvent() - proven with a
    // direct, non-dispatchEvent invocation of the captured listener
    // function in the harness). A short, stable helper - duplicating it
    // here is lower-risk than restructuring either function's scope.
    function isTyping(ev) {
      var t = ev.target;
      return !!t && (t.isContentEditable ||
        /^(input|textarea|select)$/i.test(t.tagName || ""));
    }

    // BW-309 step 6d: the same overflow/scale math layoutPan() uses,
    // independent of it (deliberately not refactored into a shared
    // helper both call, to avoid touching layoutPan()'s own already-proven
    // code path). BW-309 step 6g4/6i: box comes from dom.scenes (the
    // never-inline-stretched .ws-scenes wrapper) - see layoutPan()'s own
    // comment; dom.hotspots' own rect is post-stretch in both width AND
    // height now.
    function panGeometry(scene) {
      var dims = IMG_DIMS[scene.art];
      if (!dims) return null;
      var box = dom.scenes.getBoundingClientRect();
      if (!box.height) return null;
      var boxWidth = box.width;
      var scale = wideScale(dims, box.width, box.height);
      var dispW = dims.w * scale;
      var overflow = Math.max(0, dispW - boxWidth);
      return { overflow: overflow, dispW: dispW, boxWidth: boxWidth };
    }

    // BW-309 step 6d: keyboard reach. A hotspot is laid out once assuming
    // the CENTRE rest (coverRect() in layoutHotspots() below), then the
    // whole .ws-hotspots layer is rigidly translateX()'d by --ws-panx - so
    // "is this hotspot visible at rest f" is a question about image-space
    // windows, not about the anchor's own screen rect. At f the visible
    // image-space window is [f*(1-visW), f*(1-visW)+visW], where
    // visW = frame-width / displayed-image-width (derived the same way
    // for f=0/0.5/1 as layoutPan()'s own panX formula - left-aligned,
    // centred, right-aligned).
    function panWindowFor(f, visW) {
      var start = f * (1 - visW);
      return { start: start, end: start + visW, center: start + visW / 2 };
    }

    // BW-309 step 6e: how much of a [cx-half, cx+half] plate is actually
    // inside the visible window at rest f - the focus-follows-pan
    // fallback's new criterion once no single rest can fully contain a
    // wide plate (replacing the old nearest-by-centre-distance fallback).
    function visibleWidth(cx, half, f, visW) {
      var w = panWindowFor(f, visW);
      var lo = Math.max(cx - half, w.start);
      var hi = Math.min(cx + half, w.end);
      return Math.max(0, hi - lo);
    }

    // Focus-follows-pan: tabbing (or a screen reader's virtual cursor) onto
    // a hotspot anchor snaps to whichever rest actually shows it, so
    // keyboard reach isn't limited to whatever the shopper last dragged to.
    // Ignores while a drag is in flight (never fights a live pan) and
    // cancels an in-flight peek first (shopper intent - here, keyboard
    // navigation - always wins over the one-off cosmetic animation).
    //
    // BW-309 step 6g: the in-flight check used to be sc.classList.contains
    // ("is-panning") - but that class is only added once a drag LOCKS
    // (crosses the 8px/angle intent threshold in pointermove), not at
    // pointerdown. A real touch starting directly on a hotspot anchor can
    // focus it (iOS Safari focuses a tappable element on touch-down, before
    // any movement) in that unlocked window - focusin's own snapTo() then
    // writes --ws-panx straight to that hotspot's rest, completely
    // unknown to the drag's already-captured panDrag.startPanX, so the
    // very next pointermove computes its target from the STALE pre-focus
    // baseline and yanks the picture back - a visible glitch (found live:
    // Adam's "screen judders but doesn't move", reproduced by focusing a
    // hotspot immediately after touchstart, before any pointermove, via
    // real CDP touch emulation). panDrag itself is set synchronously at
    // pointerdown, before any lock, so checking it directly (not the class)
    // closes the gap for the whole gesture, not just its locked portion.
    dom.hotspots.addEventListener("focusin", function (ev) {
      var elm = ev.target.closest(".ws-hotspot");
      if (!elm) return;
      var sceneId = store.get().scene;
      if (!wideSwipeActive(sceneId)) return;
      var sc = dom.scenes.querySelector('[data-scene="' + sceneId + '"]');
      if (!sc || panDrag) return;
      var idx = Array.prototype.indexOf.call(dom.hotspots.children, elm);
      var def = dom.hotspotDefs && dom.hotspotDefs[idx];
      // A published hotspot with no wide `rect` (portrait-only) is already
      // display:none in wide-swipe mode (layoutHotspots()'s strictFrame
      // branch) and so can never actually receive focus - defensive only.
      if (!def || !def.rect) return;
      var geo = panGeometry(graph.scenes[sceneId]);
      if (!geo || geo.overflow < 1) return;
      var visW = geo.boxWidth / geo.dispW;
      var cx = def.rect[0] + def.rect[2] / 2;
      var half = def.rect[2] / 2;
      var current = PAN_F[sceneId] != null ? PAN_F[sceneId] : 0.5;
      var rests = RESTS;
      // BW-309 step 6d: "contains" checks the hotspot's own FULL width
      // against the window, not just its centre - a centre that's barely
      // inside a window's edge still leaves that edge of the anchor
      // hanging outside the frame once the rigid translateX is applied
      // (found live: several narrow Long Room shelf plaques whose centre
      // sat within a millimetre of a window boundary). Centre-only was the
      // plan's stated method for the general MAPPING; full-width
      // containment is what "fully inside the viewport" (the actual goal,
      // and the tester's own check) requires.
      var containing = rests.filter(function (f) {
        var w = panWindowFor(f, visW);
        return (cx - half) >= w.start - 1e-6 && (cx + half) <= w.end + 1e-6;
      });
      var targetF;
      if (containing.length) {
        // Prefer staying put if the current rest already shows it, rather
        // than hopping to the first containing rest found.
        targetF = containing.indexOf(current) !== -1 ? current : containing[0];
      } else {
        // BW-309 step 6e: no rest can fully contain this hotspot's width
        // (it's wider than a single window). With seven rests to choose
        // from instead of three, "nearest by centre distance" can pick a
        // rest that clips MORE of the plate than a neighbour would just
        // because its window centre is closer - so the fallback now picks
        // whichever rest maximises the plate's own VISIBLE width instead.
        targetF = rests.reduce(function (best, f) {
          var vis = visibleWidth(cx, half, f, visW);
          var bestVis = visibleWidth(cx, half, best, visW);
          return vis > bestVis ? f : best;
        }, 0.5);
      }
      if (targetF === current) return;
      if (cancelActivePeek) cancelActivePeek();
      PAN_F[sceneId] = targetF;
      snapTo(sc, geo.overflow, targetF);
    });

    // Arrow-key reach: ArrowLeft/ArrowRight move one rest at a time
    // (clamped at either end) when wide-swipe mode is active - inert in
    // landscape or with the setting off (wideSwipeActive() covers both),
    // while typing in a form field, or while the Browse panel is open
    // (s.browseOpen) - the shelf overlay's own scene ("shelf") carries no
    // art at all (BW-162), so wideSwipeActive() is already false there
    // with no separate check needed. preventDefault() only fires once a
    // move is actually going to happen, so an already-clamped Left/Right
    // at either end still lets the key do whatever else it would (e.g.
    // scroll) rather than silently swallowing it.
    root.addEventListener("keydown", function (ev) {
      if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
      if (isTyping(ev)) return;
      var s = store.get();
      if (s.browseOpen) return;
      var sceneId = s.scene;
      if (!wideSwipeActive(sceneId)) return;
      var current = PAN_F[sceneId] != null ? PAN_F[sceneId] : 0.5;
      // BW-309 step 6e: step by one REST index, clamped by index bounds,
      // not a flat +/-0.5 - with seven rests a flat step would overshoot
      // by two rests at a time.
      var curIdx = RESTS.indexOf(current);
      if (curIdx === -1) {
        // current isn't exactly one of the RESTS values (shouldn't happen
        // in practice, but be safe) - snap to the nearest index first.
        curIdx = 3;
        var bestDist = Infinity;
        RESTS.forEach(function (f, i) {
          var d = Math.abs(f - current);
          if (d < bestDist) { bestDist = d; curIdx = i; }
        });
      }
      var targetIdx = curIdx + (ev.key === "ArrowLeft" ? -1 : 1);
      targetIdx = Math.max(0, Math.min(REST_COUNT - 1, targetIdx));
      var targetF = RESTS[targetIdx];
      if (targetF === current) return;
      var sc = dom.scenes.querySelector('[data-scene="' + sceneId + '"]');
      if (!sc) return;
      var geo = panGeometry(graph.scenes[sceneId]);
      if (!geo || geo.overflow < 1) return;
      ev.preventDefault();
      if (cancelActivePeek) cancelActivePeek();
      PAN_F[sceneId] = targetF;
      snapTo(sc, geo.overflow, targetF);
    });

    function build() {
      var firstPaint = root.querySelector(".ws-firstpaint");
      root.innerHTML = "";
      root.className = "ws-frame is-booting";
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { root.classList.remove("is-booting"); });
      });
      if (firstPaint && store.get().scene === "storefront") root.appendChild(firstPaint);

      dom.scenes = el("div", "ws-scenes");
      graph.order.forEach(function (id) {
        var sc = el("div", "ws-scene");
        sc.dataset.scene = id;
        // BW-238: one blurred-placeholder child per scene, painted beneath
        // the scene's own background-image (walkable-2.css .ws-scene-lqip)
        // and faded out by applySceneArt/whenArtReady once the full art has
        // loaded and decoded. Present for every scene, not just ones with a
        // published placeholder - applySceneArt no-ops it (leaves its
        // background-image unset) when PLACEHOLDERS has no entry.
        sc.appendChild(el("div", "ws-scene-lqip"));
        dom.scenes.appendChild(sc);
      });
      root.appendChild(dom.scenes);
      // Only the scene you land on downloads before first paint; the rest
      // prefetch at idle once the page has settled (below).
      applySceneArt(store.get().scene);
      prefetchScenes();

      root.appendChild(el("div", "ws-vign"));
      root.appendChild(el("div", "ws-moodwash"));

      dom.hotspots = el("div", "ws-hotspots");
      root.appendChild(dom.hotspots);
      // BW-167: render() toggles root's has-beam class (line ~942, below)
      // and, in the same synchronous tick, renderHotspots() -> layoutHotspots()
      // measures `dom.hotspots.getBoundingClientRect()` to map every hotspot's
      // image-space rect into box-space percentages. `.has-beam .ws-hotspots`
      // (walkable-2.css) animates `top` - and therefore this box's height,
      // since `bottom` stays pinned at inset:0 - over a .5s transition, so
      // that synchronous read captures the box's PRE-transition size, not the
      // settled one. The Long Room and Storefront both carry beam:true, so
      // walking in the front door never toggles the class and this never
      // showed there; the shelf carries no beam, so walking back from it
      // (BW-163) does toggle it, and every Long Room hotspot got laid out
      // against the wrong (shelf-shaped) box - permanently, since nothing
      // else re-measures until a window resize. Re-running the same layout
      // once this element's own `top` transition actually finishes corrects
      // it against the settled frame.
      dom.hotspots.addEventListener("transitionend", function (e) {
        if (e.target !== dom.hotspots || e.propertyName !== "top") return;
        var scene = graph.scenes[store.get().scene];
        if (scene) layoutHotspots(scene);
        layoutPlaques();
      });
      // BW-309 step 6i: the same fix, mirrored onto dom.scenes - once
      // layoutPan() started inline-stretching dom.hotspots' own top (to
      // bottom-anchor a zoomed-out image), that inline value overrides
      // the `.has-beam .ws-hotspots { top: var(--ws-beam-h) }` class rule
      // outright, which can pre-empt the natural class-driven transition
      // the listener above is watching for on dom.hotspots specifically -
      // dom.scenes is never inline-styled by anything in this file, so
      // its own top transition always plays out untouched and remains a
      // reliable signal that the beam offset has actually settled.
      dom.scenes.addEventListener("transitionend", function (e) {
        if (e.target !== dom.scenes || e.propertyName !== "top") return;
        var scene = graph.scenes[store.get().scene];
        if (scene) layoutHotspots(scene);
        layoutPlaques();
      });

      // Shelf plaques (BW-109): sections/walkable-store.liquid renders these
      // as real <a> anchors OUTSIDE `.ws-frame` for exactly this reason - the
      // root.innerHTML = "" above runs once, at construction, so grafting the
      // Liquid-built node in now (after the wipe, not before) is what keeps
      // it out of the blast radius without ever touching its markup.
      dom.plaques = document.getElementById("ws-plaques");
      if (dom.plaques) root.appendChild(dom.plaques);

      dom.overlay = el("div", "ws-overlay");
      root.appendChild(dom.overlay);

      buildHud();
      buildBeam();
      buildNavSign();
      buildBrowsePanel();
      buildMobile();
      buildAssistant();
      buildControls();
    }

    // ---- The bookstore assistant: a corner bust medallion, the same one the
    // shelf and search pages use (BW-114 rework - she was previously painted
    // into the room with an in-room click-area; that composite drifted from
    // the reference figure on regen, so she is a widget everywhere now, not a
    // room fixture; BW-352 rebuilt her as Trish, with the expression table
    // below). She is built by assets/assistant.js, which those other pages
    // mount too - so the conversation she is having here is the same one that
    // follows the shopper to the shelf she sends them to.
    //
    // When she names a genre this room already knows, we walk there in-world
    // rather than leaving the scene, and then tell her what was on the shelf.
    function buildAssistant() {
      if (!window.BookwormsAssistant || !DATA.assistantEndpoint) return;
      // BW-352 fix: mount already knowing the landing scene's own mode
      // (mirroring how the collection/search mount gets "mini" for free by
      // never passing `mode` at all), rather than always mounting hidden and
      // waiting on the first render() call - store.subscribe(render) hasn't
      // fired yet at this point in build(), so a mount that starts hidden is
      // relying entirely on that first render() to ever reveal her; a scene
      // graph/store desync there left the corner medallion permanently
      // display:none with no ws-assist--mini class even though mount()
      // itself never threw. The Storefront scene deliberately carries no
      // `assistant` field at all (she minds the shop, not the street), so
      // this still resolves to null - and therefore hidden - there.
      var landingScene = graph.scenes[store.get().scene];
      dom.assist = window.BookwormsAssistant.mount({
        host: root,
        endpoint: DATA.assistantEndpoint,
        faces: DATA.assistantFaces,
        greeting: DATA.assistantGreeting,
        mode: (landingScene && landingScene.assistant) || null,
        onSearch: function (search) {
          if (search.kind === "collection") {
            var genre = genreByHandle(search.handle);
            if (genre) {
              actions.openCollection(genre, [ROOM]);
              dom.assist.report({ label: genre.title, count: genre.count || 0 });
              return true;
            }
          }
          // A search, or a shelf this room has no board for: let the browser go
          // there. Offline there is nowhere to go, so swallow it.
          return !DATA.shopify;
        }
      });
    }

    // ---- Top beam: a wooden lintel across the top of the long room carrying
    // the shop identity (name + tagline), contact details (phone / email) and
    // the account + bag actions. Shown only on scenes flagged `beam`.
    // BW-315: the markup itself now lives in one place,
    // snippets/masthead-beam.liquid, server-rendered by
    // sections/walkable-store.liquid into a hidden #ws-beam-slot outside
    // .ws-frame (survives the root.innerHTML = "" wipe, same technique as
    // #ws-plaques - see build()'s dom.plaques comment). This just grafts
    // that one child in as dom.beam and wires the interactive bits;
    // fillBeam() (below) is still the only thing that ever writes
    // text/href content into it, unchanged by this ticket.
    function buildBeam() {
      var slot = document.getElementById("ws-beam-slot");
      dom.beam = slot && slot.firstElementChild;
      if (!dom.beam) { dom.beam = el("div", "ws-beam"); }
      dom.beam.querySelector(".ws-beam-bag").addEventListener("click", function () { actions.openCart(); });
      dom.beam.querySelector(".ws-beam-browse").addEventListener("click", function () { actions.toggleBrowsePanel("full"); });
      // BW-205: a real text field, not a link - typing never navigates. Below
      // 1280px (see the media query in assets/masthead-beam.css) the pill
      // collapses to just the icon; a first click on it only expands the
      // field and focuses it (isTyping() already keeps the global Escape
      // handler above off any input, so the field's own Escape handler
      // below is the one that clears/collapses it), a second click submits
      // like any submit button. At full width the field is always
      // expanded, so `is-expanded` is inert there - the CSS only reads it
      // inside that media query.
      (function () {
        var form = dom.beam.querySelector(".ws-beam-search");
        var input = form.querySelector(".ws-beam-search-input");
        var btn = form.querySelector(".ws-beam-search-btn");
        function collapseIfEmpty() {
          if (!input.value.trim()) form.classList.remove("is-expanded");
        }
        form.addEventListener("submit", function (ev) {
          if (!DATA.shopify) { ev.preventDefault(); return; }
          if (!input.value.trim()) { ev.preventDefault(); input.focus(); }
        });
        btn.addEventListener("click", function (ev) {
          if (!form.classList.contains("is-expanded")) {
            ev.preventDefault();
            form.classList.add("is-expanded");
            input.focus();
          }
        });
        input.addEventListener("blur", function () {
          // Deferred: a click on the submit button fires this blur first: give
          // the click/submit handlers above a turn before deciding to collapse.
          window.setTimeout(collapseIfEmpty, 0);
        });
        input.addEventListener("keydown", function (ev) {
          if (ev.key !== "Escape") return;
          input.value = "";
          collapseIfEmpty();
          input.blur();
        });
      })();
      dom.beam.querySelector(".ws-beam-acct").addEventListener("click", function (ev) {
        if (!DATA.shopify) ev.preventDefault();
      });
      // dom.beam itself was never hidden (only its now-empty #ws-beam-slot
      // wrapper was) - .ws-beam's own base display:none / .is-shown toggle
      // (render(), unaffected by this ticket) already governs visibility.
      root.appendChild(dom.beam);
    }

    // ---- Hanging nav sign on the masthead beam: a diegetic hamburger-glyph
    // plaque (assets/menu-plaque-3.png, BW-193 - no "MENU" text in the art, so
    // its accessible name comes from aria-label/alt below) that, on
    // hover/focus/tap, drops the chained page-nav planks (assets/nav-sign-2.png)
    // down from behind the beam.
    // Each plank carries one transparent clickable row (page links), positioned
    // from NAV_PLANKS. Clicking a plank plays the book page-turn transition.
    // Raise the planks back under the plaque after a pick. is-stowed outranks the
    // :hover reveal (the pointer is still on the sign); blur releases
    // :focus-within, and is-open clears the tap-pinned state. is-stowed itself
    // is cleared on the next genuine open intent - pointerleave/pointerenter
    // for a mouse, focusin for a keyboard (below, BW-204 tester gate) - never
    // here, or a click-close with the pointer still resting on the button
    // would immediately reopen itself (the exact bug this function exists to
    // fix).
    function stowNavSign() {
      var sign = dom.navsign;
      if (!sign) return;
      sign.classList.add("is-stowed");
      sign.classList.remove("is-open");
      sign.querySelector(".ws-navsign-tab").setAttribute("aria-expanded", "false");
      if (sign.contains(document.activeElement)) document.activeElement.blur();
    }

    function buildNavSign() {
      dom.navsign = el("div", "ws-navsign");

      var tab = el("button", "ws-navsign-tab");
      tab.type = "button";
      tab.setAttribute("aria-expanded", "false");
      tab.setAttribute("aria-label", "Menu");
      var plaque = el("img", "ws-navsign-plaque");
      plaque.src = ASSETS.menuPlaque; plaque.alt = "Menu"; plaque.draggable = false;
      tab.appendChild(plaque);
      dom.navsign.appendChild(tab);

      var drop = el("div", "ws-navsign-drop");
      var img = el("img", "ws-navsign-img");
      img.src = ASSETS.navSign; img.alt = ""; img.draggable = false;
      drop.appendChild(img);
      var links = el("div", "ws-navsign-links");
      PAGES.slice(0, NAV_PLANKS.length).forEach(function (p, i) {
        var a = el("a", "ws-navlink");
        a.href = p.url || "#";
        a.title = p.title;
        a.style.top = (NAV_PLANKS[i] * 100) + "%";
        a.addEventListener("click", function (ev) {
          ev.preventDefault();
          if (isHomeUrl(p.url)) {
            // Storefront -> step back out to the street (no reload); keep the menu.
            // Put the book down first: camera.goTo is a no-op when we are already
            // on the storefront (the shop's default scene), so render() - and its
            // scene-change close - would never run.
            if (dom.closeBook) dom.closeBook();
            camera.goTo("storefront", { forward: false });
          } else {
            openPage(p.url || "#", p.title);
          }
          stowNavSign();
        });
        links.appendChild(a);
      });
      drop.appendChild(links);
      dom.navsign.appendChild(drop);

      // Hover / keyboard focus reveal the planks via CSS (:hover / :focus-within);
      // a tap pins them open on touch devices. Closing re-uses stowNavSign() -
      // toggling .is-open off on its own isn't enough: the click that closed it
      // leaves the pointer still over the button (:hover) and/or the button
      // still focused (:focus-within), either of which keeps the CSS reveal
      // matching and the drop open regardless of aria-expanded (found live,
      // BW-193 tester gate - the plank-click close path already avoided this
      // via stowNavSign()'s is-stowed + blur, the tab's own toggle never did).
      tab.addEventListener("click", function () {
        if (dom.navsign.classList.contains("is-open")) {
          stowNavSign();
        } else {
          dom.navsign.classList.remove("is-stowed");
          dom.navsign.classList.add("is-open");
          tab.setAttribute("aria-expanded", "true");
        }
      });

      // once the pointer is off the sign, hover can reveal it again
      dom.navsign.addEventListener("pointerleave", function () {
        dom.navsign.classList.remove("is-stowed");
      });
      // BW-204 tester gate: is-stowed and the :hover/:focus-within/is-open
      // reveal rule are equal CSS specificity, so is-stowed wins by source
      // order (deliberately - see the comment above stowNavSign) until
      // something clears it. pointerleave alone covers a mouse user who
      // closed by clicking-while-hovering then moves off and back - but two
      // paths it does NOT cover both read as "permanently stuck closed":
      //   1. Keyboard-only: stowNavSign()'s blur() moves focus OFF the sign,
      //      but nothing ever moved it back on again - a keyboard user who
      //      closes the menu once found :focus-within going true on the
      //      next Tab-back exactly as before, but is-stowed still set and
      //      still winning by source order.
      //   2. A mouse hover that never actually LEFT-then-returned - e.g. the
      //      very first hover after a keyboard-close, where the pointer is
      //      entering the tab for the first time, not re-entering it, so
      //      pointerleave never fired to begin with.
      // Fixed by also clearing on the two matching ENTERING edges - focusin
      // for keyboard, pointerenter for a fresh hover - rather than only the
      // leaving edge. Neither fires from stowNavSign()'s own blur() (that's
      // a focusOUT) or from continuing to hover through a click (entering
      // doesn't refire while the pointer stays put), so this can't undo the
      // suppression stowNavSign() just set up within the same close action -
      // it only clears is-stowed for the NEXT, later, genuine open intent,
      // matching pointerleave's own timing.
      dom.navsign.addEventListener("pointerenter", function () {
        dom.navsign.classList.remove("is-stowed");
      });
      dom.navsign.addEventListener("focusin", function () {
        dom.navsign.classList.remove("is-stowed");
      });

      root.appendChild(dom.navsign);
    }

    // ---- Opening a page menu item: planks navigate (BW-416). The hardback book
    // overlay below (openBook/flipTo) is parked; only contact_posted opens it.
    function openPage(url, title) {
      // BW-416: planks navigate like the footer links; book overlay parked for go-live.
      if (DATA.shopify && url && url !== "#") window.location.href = url;
    }

    function openBook(handle, title) {
      if (dom.book) return;
      var ov = el("div", "ws-tome-ov");
      // role/aria-modal live on the overlay (not .ws-tome): the close button
      // is a sibling of .ws-tome, i.e. outside it - putting the dialog role on
      // .ws-tome left the close button outside the announced dialog.
      ov.setAttribute("role", "dialog");
      ov.setAttribute("aria-modal", "true");
      ov.setAttribute("aria-labelledby", "ws-tome-title");
      ov.innerHTML =
        '<button class="ws-tome-close" aria-label="Close">&times;</button>' +
        '<div class="ws-tome">' +
          '<div class="ws-tome-stage">' +
            '<div class="ws-tome-spread"><div class="ws-tome-flip" tabindex="-1"></div></div>' +
            '<div class="ws-tome-cover" style="background-image:url(' + ASSETS.bookCover + ')"></div>' +
          '</div>' +
        '</div>';
      root.appendChild(ov);
      dom.book = ov;
      openBookHandle = handle;
      pushView();   // BW-380: /pages/<handle>

      var contentEl = ov.querySelector(".ws-tome-flip");
      renderSpread(contentEl, handle, title);

      // Trap Tab inside the book; restore to the MENU plaque, not the plank
      // that opened it - stowNavSign() has raised the planks behind the
      // plaque by the time this closes, so restoring to the plank would park
      // focus on an invisible element.
      var focusTrap = window.BookwormsFocus.trap(ov, {
        initial: contentEl,
        restore: dom.navsign && dom.navsign.querySelector(".ws-navsign-tab")
      });

      // fly in, then let the cover fall open
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { ov.classList.add("is-in"); });
      });
      setTimeout(function () { ov.classList.add("is-open"); }, 620);

      // close interactions. Exposed as dom.closeBook so a scene change can shut
      // the book too - the MENU sign sits above it, so a plank pick (or Q/M/F)
      // would otherwise swap the room out behind an open book.
      var close = function () {
        if (!dom.book) return;
        dom.book = null; dom.closeBook = null;
        openBookHandle = null;
        pushView();   // BW-380: back to whatever scene/collection is current
        document.removeEventListener("keydown", onKey);
        window.removeEventListener("resize", onResize);
        focusTrap.release();
        ov.classList.remove("is-open"); ov.classList.add("is-closing");
        setTimeout(function () { ov.remove(); }, 380);
      };
      dom.closeBook = close;
      var onKey = function (e) { if (e.key === "Escape") close(); };
      // a late-loading image, or a resized window, changes what fits - re-fit
      var onResize = function () { fitSpread(contentEl); };
      ov.querySelector(".ws-tome-close").addEventListener("click", close);
      ov.addEventListener("click", function (e) { if (e.target === ov) close(); });
      ov.addEventListener("load", onResize, true);
      document.addEventListener("keydown", onKey);
      window.addEventListener("resize", onResize);

      // intercept internal page links -> flip to that spread instead of leaving
      contentEl.addEventListener("click", function (e) {
        var a = e.target.closest("a"); if (!a) return;
        var h = handleFromUrl(a.getAttribute("href"));
        if (h && PAGE_CONTENT[h]) { e.preventDefault(); flipTo(ov, contentEl, h); }
      });
    }

    function renderSpread(contentEl, handle, title) {
      var pc = PAGE_CONTENT[handle] || {};
      // Stable id so the overlay's aria-labelledby keeps pointing at the
      // right heading across a flipTo() (which re-renders this every turn).
      contentEl.innerHTML =
        '<h2 class="ws-tome-title" id="ws-tome-title">' + (title || pc.title || "") + '</h2>' +
        '<div class="ws-tome-body">' + (pc.body || "") + '</div>';
      fitSpread(contentEl);
    }

    // The whole page has to be readable on the two open leaves. Content that
    // doesn't fit spills into a third column laid out past the right edge, where
    // overflow:hidden eats it - so shrink the type (and, with it, the cap on any
    // photo) until the columns sit inside the spread. Only ~13 steps, and it
    // reruns on resize and once each image has loaded and taken its real height.
    var FIT_MIN = 0.52, FIT_STEP = 0.04, IMG_CAP = 0.44;

    function spills(el) {
      return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
    }

    function fitSpread(contentEl) {
      if (!contentEl) return;
      contentEl.style.fontSize = "";
      contentEl.style.removeProperty("--imgcap");
      var base = parseFloat(getComputedStyle(contentEl).fontSize) || 16;
      for (var scale = 1; spills(contentEl) && scale > FIT_MIN; ) {
        scale -= FIT_STEP;
        contentEl.style.fontSize = (base * scale).toFixed(2) + "px";
        contentEl.style.setProperty("--imgcap", (IMG_CAP * scale).toFixed(3));
      }
    }

    // Turn to another page in the book: the spread rotates edge-on, swaps its
    // content, then rotates back - a page being turned.
    function flipTo(ov, contentEl, handle, title) {
      if (!contentEl || !PAGE_CONTENT[handle]) return;
      openBookHandle = handle;
      pushView();   // BW-380: turning to a different page changes /pages/<handle>
      contentEl.classList.add("is-turning");
      setTimeout(function () {
        renderSpread(contentEl, handle, title || PAGE_CONTENT[handle].title);
        requestAnimationFrame(function () { contentEl.classList.remove("is-turning"); });
      }, 260);
    }

    // ---- The trail: the aisle sign hanging over your head, naming where you
    // are and - on every step but the last - the way back to it. It is the ONLY
    // backward control in the world layer, so it shows on exactly the scenes you
    // can leave (the shelf) and never on the street or the room itself.
    function buildTrail() {
      var t = el("nav", "ws-trail");
      t.setAttribute("aria-label", "Trail");
      t.appendChild(el("div", "ws-trailboard"));
      return t;
    }

    function renderTrail(s) {
      var board = dom.trail.querySelector(".ws-trailboard");
      var steps = s.trail || [];
      root.classList.toggle("has-trail", steps.length > 1);
      board.innerHTML = "";
      if (steps.length < 2) return;
      steps.forEach(function (step, i) {
        var last = i === steps.length - 1;
        if (i) board.appendChild(el("span", "ws-trail-sep", "·"));
        if (last) {
          board.appendChild(el("span", "ws-trail-here", step.label));
          return;
        }
        var b = el("button", "ws-trail-step", (i ? "" : "&larr; ") + step.label);
        b.type = "button";
        // the path this step leads back to is the trail up to and including it,
        // so the sign that reappears there names that place, not this one
        var back = steps.slice(0, i + 1);
        b.addEventListener("click", function () { actions.walkTo(step, back); });
        board.appendChild(b);
      });
    }

    function buildHud() {
      var hud = el("div", "ws-hud");
      dom.wm = el("div", "ws-wm", '<span class="ws-worm"></span>Bookworms');
      dom.loc = el("div", "ws-loc",
        '<div class="ws-loc-b"></div><div class="ws-loc-s"></div>');
      dom.trail = buildTrail();
      // Names the focused hotspot. No keycap: it is clicked, not pressed.
      dom.prompt = el("div", "ws-prompt", '<span class="ws-prompt-txt"></span>');
      dom.cart = el("div", "ws-cart", 'Bag <span class="n">0</span>');
      dom.cart.addEventListener("click", function () { actions.openCart(); });
      hud.appendChild(dom.wm);
      hud.appendChild(dom.loc);
      hud.appendChild(dom.cart);
      hud.appendChild(dom.prompt);
      root.appendChild(hud);
      // outside the HUD: hiding the HUD must never hide the way back
      root.appendChild(dom.trail);
    }

    function buildControls() {
      var c = el("div", "ws-controls");
      dom.moodBtn = el("button", "ws-ctl", "Mood: Overcast");
      dom.moodBtn.addEventListener("click", function () { actions.cycleMood(); });
      c.appendChild(dom.moodBtn);
      root.appendChild(c);
    }

    // ---- Browse panel (BW-117): the masthead's full-catalogue Miller
    // dropdown - Category tabs (Fiction/Non-Fiction) over a Genre column and
    // a Sub-genre column that slides open beside it, replacing the two
    // wall-mounted boards BW-115 removed (Concept 1, BW-113). Same
    // graph.fiction/graph.nonfiction data those used. Desktop: opened from
    // the beam's Browse pill (buildBeam()). Mobile: the Shelves tab opens
    // this SAME panel in "mobile" mode (buildMobile()), which stops the
    // drill-down at Genre - every genre row becomes a real link straight to
    // its shelf, no sub column; sub-genre refinement moves to the facet
    // rail's bottom sheet once there (BW-108).
    function buildBrowsePanel() {
      // BW-258: the shell (dialog/tabs/columns) and its repaint now live in
      // assets/genre-drawer.js, shared with the watch-list form's own
      // "Pick a genre" drawer - this just supplies the scene's two fixed
      // categories and wiring back into actions/store.
      dom.browse = window.BookwormsGenreDrawer.buildShell(root, {
        title: "Browse",
        ariaLabel: "Browse the catalogue",
        categories: [{ key: "fiction", label: "Fiction" }, { key: "nonfiction", label: "Non-Fiction" }],
        allHref: "/collections",
        allLabel: "All shelves &rarr;",
        onClose: function () { actions.closeBrowsePanel(); },
        onCategory: function (key) { actions.setBrowseCat(key); },
        onAllClick: DATA.shopify ? null : function (e) { e.preventDefault(); }
      });
    }

    // ---- mobile chrome ----
    function buildMobile() {
      dom.mtop = el("div", "ws-mtop", '<span class="ws-worm"></span><span class="ws-mtitle">Bookworms</span>');
      dom.mloc = el("div", "ws-mloc");
      dom.mact = el("button", "ws-mact");
      dom.mact.addEventListener("click", function () { actions.primary(); });
      // The room is the hub, so it is the first tab. The street is reached from
      // the MENU sign's Storefront plank, exactly as on desktop - leaving the
      // shop should be a deliberate act, not a thumb's reach from the shelves.
      // Search (BW-105) sits between Shelves and Bag - a real search entry
      // point, not just the assistant - and leaves the room like Bag does
      // (there is no in-world search scene to walk to). BW-117: Shelves no
      // longer walks straight to the shelf scene - it opens the Browse
      // panel's Category -> Genre picker (mobile mode, no sub column);
      // picking a genre is a real link to its shelf.
      var tabs = el("div", "ws-mtabs");
      [["longroom", "▣", "Store"], ["shelf", "▤", "Browse"], ["search", "⌕", "Search"],
       ["cart", "❋", "Bag"]].forEach(function (t) {
        var b = el("button", "ws-mtab", '<i>' + t[1] + "</i>" + t[2]);
        b.dataset.target = t[0];
        b.addEventListener("click", function () {
          if (t[0] === "cart") actions.openCart();
          else if (t[0] === "search") actions.openSearch();
          else if (t[0] === "shelf") actions.toggleBrowsePanel("mobile");
          else actions.goToScene(t[0]);
        });
        tabs.appendChild(b);
      });
      dom.mtabs = tabs;
      root.appendChild(dom.mtop);
      root.appendChild(dom.mloc);
      root.appendChild(dom.mact);
      root.appendChild(tabs);
    }

    // ---------------------------------------------------------- BW-380: URL/history sync
    // Keeps the address bar and Back/Forward in step with the walkable
    // scene, without touching Browse-panel state, category/genre/mood or
    // wide-swipe pan - none of that is part of "where you are" for a
    // bookmark or a Back press. A pushed state is a small tagged object -
    // `ws: 1` distinguishes it from assets/pdp-modal.js's own
    // `{pdpModal: true}` history entries, which this file never touches:
    //   { ws: 1, scene, collectionUrl, collectionName, book }
    // `book` is the open page-book's handle (null when none is open) - it
    // lives outside the store (openBookHandle, above), so it is folded in
    // here rather than tracked by store.subscribe.
    var applying = false;   // true while a popped/replayed state is being
                             // applied - pushView() is a no-op the whole
                             // time, so replaying a past state never
                             // re-pushes it as if it were a new transition.

    function stateOf() {
      var s = store.get();
      var onShelf = s.scene === "shelf";
      return {
        ws: 1,
        scene: s.scene,
        collectionUrl: onShelf ? (s.collectionUrl || null) : null,
        collectionName: onShelf ? (s.collectionName || null) : null,
        book: openBookHandle || null
      };
    }

    // The one URL each state maps to. An open page-book takes precedence
    // over whatever scene sits behind it - Back should land wherever
    // /pages/<handle> actually is, not on a made-up "scene + book" hybrid
    // route. The modal product view (pdp-modal.js) owns /products/<handle>
    // entirely; this function never produces one.
    function urlFor(st) {
      if (st.book) return "/pages/" + st.book;
      if (st.scene === "storefront") return "/";
      if (st.scene === "longroom") return "/?scene=longroom";
      if (st.scene === "shelf") return st.collectionUrl || "/?scene=shelf";
      return "/";
    }

    function sameState(a, b) {
      return !!a && !!b && a.ws === b.ws && a.scene === b.scene &&
        (a.collectionUrl || null) === (b.collectionUrl || null) &&
        (a.book || null) === (b.book || null);
    }

    // Called after every real transition. Most of them (a scene change, or a
    // collection-to-collection jump that leaves the scene at "shelf") reach
    // here through store.subscribe(pushView) below, since both go through
    // store.set(); openBook()/close()/flipTo() above call it directly, since
    // page-book state lives outside the store. Gated on the computed state
    // actually differing from history.state, so a no-op store update (a mood
    // cycle, the HUD toggle, re-clicking the same shelf) never pushes a
    // duplicate entry.
    function pushView() {
      if (applying) return;
      var st = stateOf();
      if (sameState(window.history.state, st)) return;
      window.history.pushState(st, "", urlFor(st));
    }

    // The single re-render entry for a popped/replayed state. Reuses the
    // same actions openCollection()/goToScene() a live click already goes
    // through, rather than re-deriving trail bookkeeping here -
    // renderOverlay()'s own shownShelfUrl guard already skips re-fetching a
    // shelf that's already showing, so restoring the same collection twice
    // in a row (a pageshow replay, or a duplicate popstate) is cheap.
    function applyState(st) {
      var cur = store.get();
      var sceneOrCollectionChanged = st.scene !== cur.scene ||
        (st.scene === "shelf" && (st.collectionUrl || null) !== (cur.collectionUrl || null));
      if (sceneOrCollectionChanged) {
        if (st.scene === "shelf" && st.collectionUrl) {
          actions.openCollection({ url: st.collectionUrl, name: st.collectionName }, [ROOM]);
        } else {
          actions.goToScene(st.scene);
        }
        // Belt-and-braces: match the restored collection fields exactly,
        // even where the action above doesn't itself clear a stale one
        // (goToScene("shelf") with no collection carried, same as the
        // mobile Shelves tab already behaves).
        store.set({
          collectionUrl: st.scene === "shelf" ? (st.collectionUrl || null) : cur.collectionUrl,
          collectionName: st.scene === "shelf" ? (st.collectionName || null) : cur.collectionName
        });
      }
      // Scene restore above already auto-closes an open book on a real
      // scene change (render()'s own sceneChanged branch) - this only
      // handles the two cases that leaves open: opening the target book
      // fresh, or closing one that's open but shouldn't be (the state we're
      // restoring to had no book, e.g. Back out of a page-book).
      if (st.book) {
        if (!dom.book) openBook(st.book, null);
        else if (openBookHandle !== st.book) flipTo(dom.book, dom.book.querySelector(".ws-tome-flip"), st.book);
      } else if (dom.closeBook) {
        dom.closeBook();
      }
    }

    // ---- reactive render ----
    var shownScene = null;

    function render(s) {
      var sceneChanged = s.scene !== shownScene;
      // Walking somewhere else closes the page book - you can't stand in the
      // street still reading it. (The Storefront plank, Q, M and F all move the
      // camera from under the open book.)
      if (sceneChanged && dom.closeBook) dom.closeBook();
      shownScene = s.scene;

      // BW-309: the scene lands on the centre on every entry, before it
      // becomes .is-active, with no transition class - layoutPan() (called
      // from layoutHotspots() below, via renderHotspots()) then computes
      // the same panX from the reset PAN_F, so this is belt-and-braces
      // against any visible transition rather than load-bearing on its
      // own. The scene being LEFT keeps its own frozen inline --ws-panx
      // untouched (not reset here) - "stays where left while in it". A
      // drag still in flight when the scene changes (e.g. a hotspot walk
      // fired mid-drag) is cancelled outright: release capture, clear
      // both classes, never let a stale drag keep writing into the new
      // scene's hotspots.
      if (sceneChanged) {
        // BW-309 step 6h2: the ONE trigger (with the resize listener's
        // width branch, below) allowed to re-derive the stage's docTop -
        // see fitFrameHeight()'s own comment for why every other call site
        // that used to touch it (every layoutPan() call) was removed.
        fitFrameHeight(graph.scenes[s.scene], wideSwipeActive(s.scene));
        PAN_F[s.scene] = 0.5;
        var enteringSc = dom.scenes.querySelector('[data-scene="' + s.scene + '"]');
        if (enteringSc) {
          enteringSc.classList.remove("is-panning", "is-snapping");
          enteringSc.style.setProperty("--ws-panx", "0px");
        }
        dom.hotspots.classList.remove("is-panning", "is-snapping");
        if (panDrag) {
          try { root.releasePointerCapture(panDrag.pointerId); } catch (e) {}
          panDrag = null;
        }
      }

      // scenes
      // BW-163: the shelf carries no art of its own (BW-162 removed
      // scene-shelf.webp - the opaque book grid renderOverlay() pours into
      // .ws-overlay, z-index 9, is the entire visual while browsing a shelf)
      // and the trail's back step always names "longroom" literally (ROOM,
      // above), so the Long Room's .ws-scene layer is kept fully painted
      // (never faded to .is-leaving-forward's opacity:0) for as long as the
      // shelf is shown, instead of fading out on the way in and back in on
      // the way out. Walking back from the shelf is then just the overlay
      // wipe below (already synchronous, no transition) revealing a room
      // that was never hidden - previously that reveal raced a .9s opacity
      // fade over the shelf's now art-less (fully transparent) scene, which
      // is what exposed --frame-bg as a dark flash for most of that .9s.
      // Walking in (longroom -> shelf) looks identical either way: that
      // fade was always hidden the instant the opaque grid appears.
      var shelfShown = s.scene === "shelf";
      Array.prototype.forEach.call(dom.scenes.children, function (sc) {
        var id = sc.dataset.scene;
        var active = id === s.scene || (id === "longroom" && shelfShown);
        sc.classList.toggle("is-active", active);
        var leaving = id === s.prevScene && s.forward && !(id === "longroom" && shelfShown);
        sc.classList.toggle("is-leaving-forward", leaving);
      });
      var scene = graph.scenes[s.scene];
      applySceneArt(s.scene);   // a rotated phone swaps every scene to its portrait art

      // location + trail. On the shelf the aisle takes the name of the
      // collection you walked into, rather than always claiming to be Fiction.
      var here = s.scene === "shelf" && s.collectionName
        ? { title: s.collectionName.toUpperCase(), sub: scene.location.sub }
        : scene.location;
      dom.loc.querySelector(".ws-loc-b").textContent = here.title;
      dom.loc.querySelector(".ws-loc-s").textContent = here.sub;
      dom.mloc.textContent = here.title;
      renderTrail(s);

      // scene-flagged in-world UI props (beam / nav sign)
      root.classList.toggle("has-beam", !!scene.beam);
      // BW-309: gates the .ws-hotspots translateX rule (walkable-2.css) -
      // kept off .ws-frame/.ws-stage themselves, never used as the
      // transform target (see the containing-block hazard note on
      // .ws-mact/.ws-mtabs in walkable-2.css).
      root.classList.toggle("is-wideswipe", wideSwipeActive(s.scene));
      dom.beam.classList.toggle("is-shown", !!scene.beam);
      dom.navsign.classList.toggle("is-shown", !!scene.navSign);
      // BW-352: she is out front on the storefront scene too now, same
      // corner bust as inside - every scene in the graph sets its own
      // `assistant` field (all "mini" today; a future scene could omit it to
      // stay assistant-free, same mechanism).
      if (dom.assist) dom.assist.setMode(scene.assistant || null);
      if (scene.beam) fillBeam(s);

      // hotspots for scene
      renderHotspots(scene, s);
      // BW-309 step 6b2: the peek trigger, moved here (strictly after
      // renderHotspots() -> layoutHotspots() -> layoutPan() have already
      // laid out and written this scene's rest --ws-panx above) from the
      // earlier sceneChanged block above, where it used to run BEFORE that
      // layout pass - layoutPan() unconditionally re-derives --ws-panx
      // from PAN_F on every call, so a peek write that happened earlier in
      // the SAME render() tick was getting silently overwritten before the
      // browser ever painted it (found live by the tester: a monkey-patched
      // setProperty trace showed the peek's -56px write at t~1ms, then
      // layoutPan's own 0px re-derivation at t~4ms, same tick, no paint in
      // between). See maybeAttemptPeekOnEntry()'s own comment for the rest
      // of the picture, and runPeek()'s for the double-rAF paint-boundary
      // fix this alone doesn't cover on its own.
      if (sceneChanged) maybeAttemptPeekOnEntry(s.scene);
      renderOverlay(scene, s);
      layoutPlaques();     // shelf plaques (BW-109) - same image-space anchoring

      // prompt (focus caption)
      var focus = s.focus;
      if (focus) {
        dom.prompt.classList.add("is-shown");
        dom.prompt.querySelector(".ws-prompt-txt").textContent = focus.label;
      } else {
        dom.prompt.classList.remove("is-shown");
      }

      // the Browse panel (masthead pill on desktop, Shelves tab on mobile)
      renderBrowsePanel(s);

      // cart
      dom.cart.querySelector(".n").textContent = s.cartCount;

      // Mobile primary action = the scene's way forward. A scene with nowhere
      // further to walk (the Long Room, since BW-143; the shelf, always)
      // shows no button - the trail sign and the tab bar carry the way back,
      // and the books stay unobstructed.
      var fwd = forwardHotspot(scene);
      dom.mact.style.display = fwd ? "" : "none";
      if (fwd) dom.mact.innerHTML = fwd.label;
      // BW-352: on mobile .ws-mact spans the full width just above the tab
      // bar, right under where the assistant medallion's own in-frame
      // clearance sits - the Storefront's structural door hotspot is always
      // a `goto` (forwardHotspot() always finds it), so once she also shows
      // there the two would overlap. This class lifts her further whenever
      // the forward button is actually on screen, on any scene, rather than
      // just for the Storefront specifically (assistant.css).
      root.classList.toggle("has-forward", !!fwd);
      Array.prototype.forEach.call(dom.mtabs.children, function (b) {
        b.classList.toggle("on", b.dataset.target === s.scene);
      });

      // controls
      dom.moodBtn.textContent = "Mood: " + s.mood.charAt(0).toUpperCase() + s.mood.slice(1);
      root.classList.toggle("no-hud", !s.showHud);
    }

    // Fill the top beam from SITE + cart state (cheap; called on beam scenes).
    function fillBeam(s) {
      dom.beam.querySelector(".ws-beam-name").textContent = SITE.name || "Bookworms";
      dom.beam.querySelector(".ws-beam-tag").textContent = SITE.tagline || "";
      var ph = dom.beam.querySelector(".ws-beam-phone");
      ph.textContent = SITE.phone || "";
      ph.href = SITE.phone ? "tel:" + SITE.phone.replace(/[^+\d]/g, "") : "#";
      ph.style.display = SITE.phone ? "" : "none";
      var em = dom.beam.querySelector(".ws-beam-email");
      em.textContent = SITE.email || "";
      em.href = SITE.email ? "mailto:" + SITE.email : "#";
      em.style.display = SITE.email ? "" : "none";
      // null means customer accounts are off - no sign-in to offer at all,
      // not a link to nowhere.
      var acct = SITE.account;
      var acctEl = dom.beam.querySelector(".ws-beam-acct");
      acctEl.href = acct ? acct.url || "#" : "#";
      acctEl.style.display = acct ? "" : "none";
      dom.beam.querySelector(".ws-acct-t").textContent = (acct && acct.label) || "Account";
      dom.beam.querySelector(".ws-beam-bag .n").textContent = s.cartCount;
    }

    // Scenes ship a 21:9 `art` and a 9:16 `artPortrait`. Pick by the shape of the
    // frame, not its size: two screens of the same aspect crop identically no
    // matter how many pixels they have. Cheap to re-run - assigning the same URL
    // is a no-op, so render() can call it on every resize.
    //
    // `only` limits the assignment to one scene id (the one just shown); passed
    // undefined it does every scene, which is prefetchScenes()'s job below.
    // camera.goTo is untouched by this - render() runs on every scene change
    // and assigns the target's art before the crossfade, so the idle prefetch
    // is an optimisation, not a correctness requirement.
    //
    // BW-238: `sc.dataset.art` records the URL last assigned so the whole
    // placeholder dance below is skipped when it hasn't changed - render()
    // calls this on every state change (mood cycles, cart updates, not just
    // a scene walk), and re-running the placeholder swap on an unrelated
    // update would re-blur art that's already showing. When the URL DOES
    // change: no placeholder for this scene -> `is-art-ready` goes on
    // immediately, today's behaviour exactly; the target art is already
    // known loaded (ART_READY) -> also immediate, no blur ever paints; else
    // the placeholder paints, `is-art-ready` comes off, and whenArtReady
    // adds it back once the full image has loaded AND decoded.
    // BW-429: marks a scene's art ready; the storefront's server-rendered first-paint
    // backdrop goes once that scene's 0.9s opacity fade-in has covered it.
    function artReady(sc) {
      sc.classList.add("is-art-ready");
      var fp = root.querySelector(".ws-firstpaint");
      if (fp && sc.dataset.scene === "storefront") setTimeout(function () { fp.remove(); }, 1200);
    }
    function applySceneArt(only) {
      graph.order.forEach(function (id) {
        if (only && id !== only) return;
        var s = graph.scenes[id];
        var portrait = usesPortraitArt(id);
        var art = (portrait && s.artPortrait) || s.art;
        if (!art) return;
        var sc = dom.scenes.querySelector('[data-scene="' + id + '"]');
        if (!sc || sc.dataset.art === art) return;
        sc.dataset.art = art;
        sc.style.backgroundImage = "url('" + art + "')";

        var ph = PLACEHOLDERS[id];
        var phUri = ph && (portrait ? ph.portrait : ph.wide);
        var lqip = sc.querySelector(".ws-scene-lqip");
        if (!phUri) {
          artReady(sc);
          if (lqip) lqip.style.backgroundImage = "";
          return;
        }
        // BW-429: the sharp first-paint backdrop is showing - a blurred placeholder
        // over it would flash sharp, blur, sharp; the scene stays transparent
        // until its art decodes and fades in over the same sharp image.
        var backdropUp = id === "storefront" && root.querySelector(".ws-firstpaint");
        if (lqip && !backdropUp) lqip.style.backgroundImage = "url('" + phUri + "')";
        if (ART_READY[art]) {
          artReady(sc);
          return;
        }
        sc.classList.remove("is-art-ready");
        // BW-309 step 6b2: no maybeStartPeek() call here any more -
        // maybeAttemptPeekOnEntry() (called from render(), strictly after
        // layoutPan() has already run for this scene) is the sole peek
        // trigger now, for exactly the ordering reason its own comment
        // explains. This callback's only job is the LQIP fade.
        whenArtReady(art, function () { artReady(sc); });
      });
    }

    // Fills in the scenes the shopper hasn't walked into yet, once the page has
    // gone idle - so a slow connection isn't paying for three scenes' worth of
    // art before the first one it actually needs is even visible.
    // BW-429: waits for the active scene's art and the window load event so the
    // other scenes' fetches never compete with the first paint.
    function prefetchScenes() {
      var id = store.get().scene, s = graph.scenes[id];
      var art = (usesPortraitArt(id) && s.artPortrait) || s.art;
      var artReady = new Promise(function (resolve) { whenArtReady(art, resolve); });
      var loaded = new Promise(function (resolve) {
        if (document.readyState === "complete") resolve();
        else window.addEventListener("load", resolve, { once: true });
      });
      Promise.all([artReady, loaded]).then(function () {
        if (window.requestIdleCallback) requestIdleCallback(function () { applySceneArt(); });
        else setTimeout(function () { applySceneArt(); }, 3000);
      });
    }

    // BW-429: first intent on a walk-through hotspot starts its target's art.
    function warmTarget(h) {
      var a = h.action;
      if (a && a.type === "goto" && graph.scenes[a.scene]) applySceneArt(a.scene);
    }

    // ---- image-space anchoring (BW-112) --------------------------------
    // Scene art renders with `background-size: cover`, so its visible crop
    // shifts with the box's aspect ratio - a box relatively wider than the
    // image trims the sides, one relatively taller trims top/bottom. Hotspot
    // rects (and the assistant's anchor point below) are authored as
    // fractions of the SOURCE IMAGE, so on every layout they have to be
    // mapped through that same cover transform against the box's current
    // pixel size - not applied directly as box percentages, which is what
    // let them drift off their objects as the window resized. One shared
    // mechanism for both, so a future art-anchored overlay (e.g. Phase 1's
    // beam labels) can reuse it rather than growing a second copy.
    // (IMG_DIMS itself is declared above, before build() - BW-238.)
    function loadImageDims(url, cb) {
      if (!url) return;
      if (IMG_DIMS[url]) { cb(IMG_DIMS[url]); return; }
      var im = new Image();
      im.onload = function () {
        IMG_DIMS[url] = { w: im.naturalWidth, h: im.naturalHeight };
        cb(IMG_DIMS[url]);
      };
      im.src = url;
    }

    // Maps an image-space rect [x,y,w,h] (0..1 fractions of the source image)
    // to box-space fractions, given the box's own pixel size - the same
    // cover-fit math `background-size: cover` uses to scale and centre-crop.
    function coverRect(rect, imgW, imgH, boxW, boxH) {
      var scale = Math.max(boxW / imgW, boxH / imgH);
      var dispW = imgW * scale, dispH = imgH * scale;
      var offX = (dispW - boxW) / 2, offY = (dispH - boxH) / 2;
      return {
        x: (rect[0] * dispW - offX) / boxW,
        y: (rect[1] * dispH - offY) / boxH,
        w: rect[2] * dispW / boxW,
        h: rect[3] * dispH / boxH
      };
    }

    // BW-316: root's height is OURS in wide-swipe mode and is routinely
    // SHORTER than root.clientWidth now (328x390 at the 60% default on a
    // 390x844 phone), so reading the frame's own current box would report
    // landscape, switch the mode off, restore the tall frame, and switch it
    // back on - an oscillation on every layout pass. Fall back to the
    // frame's own box only when we have not set a height on it ourselves.
    function frameNaturalH() {
      if (!root.style.height) return root.clientHeight;
      return Math.max(320, window.innerHeight - (root.getBoundingClientRect().top + window.scrollY));
    }
    function isPortraitFrame() { return frameNaturalH() > root.clientWidth; }

    // BW-309: true when this scene should show its WIDE capture on a
    // portrait phone (with the swipe-to-pan gesture) instead of its own
    // separate portrait capture - gated on the theme setting AND the scene
    // actually having wide art. A scene with no wide art (the shelf, which
    // carries no art of its own since BW-162) or the setting off stays
    // exactly as before this ticket.
    function wideSwipeActive(id) {
      var scene = graph.scenes[id];
      return !!WIDE_SWIPE[id] && isPortraitFrame() && !!(scene && scene.art);
    }

    // BW-309: replaces every direct `isPortraitFrame()` read that used to
    // mean "use the portrait asset/hotspots" - wide-swipe mode transparently
    // swaps in the wide capture (and its own hotspot set) wherever this now
    // returns false on a portrait phone.
    function usesPortraitArt(id) {
      return isPortraitFrame() && !wideSwipeActive(id);
    }

    // BW-309: fraction of the WIDE image that sits at frame centre, per
    // scene id - {0, 0.5, 1} once the gesture (a later build step) exists,
    // defaulting to 0.5 (centred) for any scene id not yet dragged.
    // Persists for the life of the page.
    var PAN_F = {};

    // Stretches the active .ws-scene AND .ws-hotspots to the wide image's
    // own displayed width and writes --ws-panx on both, computed from the
    // UNSTRETCHED .ws-hotspots box - identical to .ws-scenes' box, both
    // inset:0 with the same .has-beam top. Once stretched, the box's aspect
    // equals the image's exactly, so layoutHotspots()'s coverRect() below
    // degenerates to the identity and every hotspot's image-space rect maps
    // straight to its percentage - art and click areas can never desync
    // because both ride the SAME translateX on the compositor (the CSS
    // gated .is-wideswipe .ws-hotspots rule, walkable-2.css).
    //
    // Inactive (setting off, landscape, or no wide art): clears any
    // stretch/pan a previous wide-swipe-active layout left behind, so a
    // rotation back to landscape or the setting toggling off leaves no
    // trace. Rejected: background-position-x (a full-layer repaint per
    // frame, and art/hotspots would animate on different pipelines).
    //
    // BW-309 step 6h: .ws-stage's own 100dvh (BW-99) measures the FULL
    // visible viewport regardless of how much real document-flow chrome
    // (the announcement bar, the site masthead) sits above it - on a
    // supporting page that chrome pushes .ws-stage well below the fold, so
    // 100dvh from there ran the frame's true bottom off-screen (found live,
    // Adam's iPhone screenshot at zoom 85: masthead ends ~205px down, but
    // the frame kept the FULL 100dvh height measured from there instead of
    // the remaining ~205px-shorter space actually visible). In wide-swipe
    // mode only, an inline height on .ws-stage overrides 100dvh to exactly
    // the space between wherever the frame currently starts and the bottom
    // of the visible viewport, so the frame's own bottom edge lands flush
    // with the viewport's bottom - no scroll needed to see the whole scene.
    // Clamped to a sane floor so a transient short viewport (mid-resize)
    // never collapses the frame to nothing. This is the one exception to
    // BW-99's "no JS-measured offsets" rule, scoped tightly to wide-swipe
    // mode - every other path (landscape, the setting off) clears the
    // inline height and 100dvh governs exactly as BW-99 designed.
    //
    // BW-309 step 6h2: fix for a real bug in step 6h's first version.
    // getBoundingClientRect().top is VIEWPORT-relative - it goes NEGATIVE
    // once the page scrolls the frame above the viewport's own top, and
    // `viewportHeight - top` then equals viewportHeight + scrollDistance,
    // so the computed stage height (and therefore the wide-swipe height-fit
    // scale, which divides by this height) balloons without bound as the
    // shopper scrolls - found live from Adam's screen recording: scrolling
    // up from the footer showed the scene rendered at ~4x scale, flicking
    // between two sizes as scrollY (and therefore the erroneous height)
    // kept changing, then a fully BLACK frame partway through. The black
    // frame is the same bug, not a second one: once the ballooned height
    // pushes `overflow`/`panX` (layoutPan()'s own math, below) far past any
    // sane value, the visible window they define no longer overlaps the
    // actual rendered image at all - nothing left to show but --frame-bg's
    // near-black. Fix: `docTop`, the element's DOCUMENT offset
    // (`rect.top + window.scrollY`), is scroll-independent by construction
    // - reading it at any scroll position gives the same answer.
    //
    // BW-309 step 6h3: 6h2's fix (a CSS calc() against 100svh) turned out
    // to be its own bug on real iOS Safari - Adam's SECOND recording still
    // showed the room zooming in while scrolling and staying zoomed once
    // back at the top. 100svh is STILL a live CSS viewport unit; step 6h2
    // only swapped which one drove the height (dvh -> svh), it never
    // actually stopped depending on one. A full audit (BW-309 step 6h3,
    // see the commit message) found no min-height/max-height and no
    // !important anywhere on .ws-stage/.ws-frame/.ws-scenes/.ws-scene that
    // could out-cascade an inline style, so the most likely explanation is
    // real iOS Safari's svh/dvh live-recompute behaviour itself, not a CSS
    // specificity bug - not reproducible in this repo's headless-Chrome
    // harness, which is why 6h2's proof never caught it. Fix: stop using
    // ANY viewport unit in the applied value. The height is now a single
    // frozen PIXEL number (`window.innerHeight - docTop`, floor 320) that
    // cannot itself live-recompute, applied to every element in the chain
    // that can size the frame - .ws-stage AND .ws-frame both get
    // height/min-height/max-height all set to the same px value (the
    // min/max neutralise any future CSS rule that might otherwise win
    // against a plain `height`; .ws-scenes needs nothing extra, since its
    // own `inset: 0` already tracks whatever height .ws-frame ends up at
    // with no separate write). Still a frozen snapshot, still re-derived
    // ONLY from the two places that should ever change docTop for real -
    // render()'s scene-entry block and the resize listener's WIDTH branch
    // (both below) - never from layoutPan() itself, so a height-only
    // settle, a scroll, or the BW-168 has-beam transitionend hook can no
    // longer touch the frame's own height at all, only the pan geometry
    // inside whatever box that height currently measures.
    // BW-316: resolves --ws-beam-h (a clamp() off vw/vh) to its actual PIXEL
    // value. getComputedStyle(root).getPropertyValue("--ws-beam-h") returns
    // the literal clamp(...) text for a custom property, not a resolved
    // length - parseFloat on that is NaN, so beam silently read as 0 always
    // (found in the local harness: predicted and applied frame height only
    // matched once this probe replaced that approach). A throwaway
    // absolutely-positioned probe with `height: var(--ws-beam-h)` forces
    // the UA to actually resolve the clamp() against the current viewport;
    // reading has-beam is never involved (root carries the custom property
    // unconditionally), so this also sidesteps the ordering hazard of
    // render() toggling has-beam AFTER calling fitFrameHeight() on scene
    // entry (see that call site) - no race with the class or its own .5s
    // top transition on dom.scenes.
    function resolvedBeamPx() {
      var probe = document.createElement("div");
      probe.style.position = "absolute";
      probe.style.visibility = "hidden";
      probe.style.height = "var(--ws-beam-h)";
      root.appendChild(probe);
      var h = probe.getBoundingClientRect().height;
      root.removeChild(probe);
      return h;
    }
    // BW-316: takes the scene too, not just `active` - the frame now shrinks
    // to the picture (wideScale()'s width term), not just to the viewport,
    // so it needs the scene's own image dims and beam flag to compute that.
    function fitFrameHeight(scene, active) {
      if (!active) {
        stage.style.height = "";
        stage.style.minHeight = "";
        stage.style.maxHeight = "";
        root.style.height = "";
        root.style.minHeight = "";
        root.style.maxHeight = "";
        return;
      }
      var docTop = root.getBoundingClientRect().top + window.scrollY;
      var Hnat = Math.max(320, window.innerHeight - docTop);
      var beam = scene.beam ? resolvedBeamPx() : 0;
      var dims = IMG_DIMS[scene.art];
      var H;
      if (!dims) {
        // BW-316: dims not loaded yet - apply the old full-height fallback
        // for this first paint, then re-enter once loadImageDims() resolves
        // (the same re-entrant pattern layoutPan() already uses).
        H = Hnat;
        loadImageDims(scene.art, function () { fitFrameHeight(scene, active); });
      } else {
        var scale = wideScale(dims, root.clientWidth, Hnat - beam);
        H = beam + dims.h * scale;
      }
      var px = H + "px";
      stage.style.height = px;
      stage.style.minHeight = px;
      stage.style.maxHeight = px;
      root.style.height = px;
      root.style.minHeight = px;
      root.style.maxHeight = px;
    }
    function layoutPan(scene) {
      var id = scene.id;
      var sc = dom.scenes.querySelector('[data-scene="' + id + '"]');
      if (!sc) return;
      var active = wideSwipeActive(id);
      if (!active) {
        sc.style.left = "";
        sc.style.right = "";
        sc.style.top = "";
        sc.style.height = "";
        sc.style.removeProperty("--ws-panx");
        dom.hotspots.style.left = "";
        dom.hotspots.style.right = "";
        dom.hotspots.style.top = "";
        dom.hotspots.style.height = "";
        dom.hotspots.style.removeProperty("--ws-panx");
        return;
      }
      var dims = IMG_DIMS[scene.art];
      if (!dims) { loadImageDims(scene.art, function () { layoutPan(scene); }); return; }
      // BW-309 step 6g4/6i: the natural (unstretched) box comes from
      // dom.scenes - the .ws-scenes WRAPPER, not dom.hotspots itself - not
      // dom.hotspots' own rect. dom.hotspots (like each individual
      // .ws-scene) now gets its own left/right/top/height inline-stretched
      // below, so reading its box back for "the natural frame size" would
      // be self-referential: the next call would see the ALREADY-stretched
      // size as if it were natural, compounding smaller (or larger) every
      // time this runs. dom.scenes shares the exact same CSS treatment
      // (`inset: 0`, `.has-beam` top offset) but is never itself inline-
      // stretched by any code in this file, only its individual .ws-scene
      // children are - a stable reference for both width AND height (root
      // alone would be wrong for height on a has-beam scene, since it
      // doesn't account for the beam's own top offset the way dom.scenes'
      // own rect does).
      var box = dom.scenes.getBoundingClientRect();
      if (!box.height) return;
      var boxWidth = box.width;
      // BW-309 step 6g2 (revised): layoutPan() itself always does a full,
      // correct, fresh layout whenever it's actually called - exactly as
      // before step 6h ever existed. An earlier version of this fix tried
      // to skip re-deriving here whenever the box's WIDTH matched the last
      // stretch, but that froze the stretch against whatever height
      // happened to be current the FIRST time it ran (found live: a page's
      // own natural layout settle - fonts, the masthead beam sizing -
      // still shifts box.height by dozens of px in the ~1s after first
      // paint, unrelated to any address bar; freezing that early reading
      // left the stretch permanently wrong even once BW-309 step 6g2's
      // OWN later, genuinely-settled calls ran, since only a width change
      // ever lifted the freeze). The real fix belongs at the RESIZE EVENT
      // ENTRY POINTS instead (see the two resize listeners below) - they
      // now skip calling into this function at all for a height-only
      // resize, so every call that DOES reach here (scene entry, a drag,
      // a keyboard press, the peek, a genuine width/orientation change)
      // is trusted to want a real, current layout.
      //
      // BW-309 step 6b2 (revised step 6g6): an animation (the peek, a live
      // pan drag, or a snap-back release) currently owns --ws-panx - the
      // ONLY property any CSS transition here ever animates (`.is-snapping
      // { transition: transform .28s }`, driven by --ws-panx via
      // translateX()). left/right/top/height are plain static inline
      // styles, never transitioned, so writing them mid-animation cannot
      // fight anything - step 6h/6g2 once widened this guard to cover the
      // stretch too ("pure geometry, safe any time" turned out not
      // universally true for a DIFFERENT reason, a stale-freeze bug fixed
      // structurally since), but that widened guard created a new,
      // strictly worse bug: with a height-dependent beam (--ws-beam-h:
      // clamp(...vh...)) and the one-time peek both active on the SAME
      // first-ever page load, the peek's ~0.6-0.7s runtime left top/height
      // frozen at a pre-settle reading for that whole window - a real Y
      // mis-registration (up to 52px, one full beam height) on a
      // shopper's very first tap, found live by the tester sampling the
      // scene/hotspots gap with and without the peek running. So: gate
      // ONLY the final --ws-panx write below (and the overflow<1 degrade
      // branch's own panX reset) on `animating`; the stretch and vertical
      // geometry always write from the CURRENT box, animating or not.
      var animating = sc.classList.contains("is-panning")
        || sc.classList.contains("is-snapping")
        || sc.classList.contains("is-peeking");
      // BW-316: box.height here is already the frame's SHRUNK height
      // (fitFrameHeight() ran first, in render()'s scene-entry block and
      // the resize width branch, before this ever measures the box) - so
      // wideScale()'s height term is already at its cap and its width term
      // (WIDE_SWIPE_VISIBLE) is what actually governs the stretch below.
      // No band at any setting: dispH always equals box.height exactly
      // (the same formula produced both), and dispW >= boxWidth always.
      var scale = wideScale(dims, boxWidth, box.height);
      var dispW = dims.w * scale;
      var dispH = dims.h * scale;
      var overflow = Math.max(0, dispW - boxWidth);
      // BW-309 step 6i: an inline `top` on dom.hotspots OVERRIDES the
      // `.has-beam .ws-hotspots { top: var(--ws-beam-h) }` CSS rule
      // outright (inline always wins over a class rule, regardless of
      // specificity) - omitting it would silently drop the beam offset on
      // every has-beam scene (Long Room), pinning the stretched box to the
      // very top of the frame, under the masthead text itself.
      // beamOffset recovers whatever the CSS rule already contributed by
      // reading it straight off dom.scenes (never itself inline-styled) -
      // relative to root, the shared containing block.
      //
      // sc (.ws-scene) does NOT need beamOffset added: unlike dom.hotspots,
      // it never had its own `top: var(--ws-beam-h)` CSS rule - that rule
      // targets .ws-scenes (sc's own containing block, the wrapper all
      // .ws-scene children sit inset:0 inside), not .ws-scene itself. sc's
      // CONTAINING BLOCK is already sitting at the beam-offset position via
      // that CSS rule, so an inline top on sc is relative to an already-
      // shifted origin - adding beamOffset again there double-counts it
      // (found live, tester-reported: .ws-scene's rect landed 52px - one
      // full beam height - below dom.hotspots', not identical to it, at
      // every rest, confirmed live at exactly 104px vs 52px, matching
      // beamOffset + beamOffset vs beamOffset + 0). sc's top is always 0 -
      // top-anchored, no zoom-driven offset to add on this element either.
      var beamOffset = box.top - root.getBoundingClientRect().top;
      sc.style.top = "0px";
      sc.style.height = dispH + "px";
      dom.hotspots.style.top = beamOffset + "px";
      dom.hotspots.style.height = dispH + "px";
      if (overflow < 1) {
        // A future narrower wide master (or a near-square viewport)
        // degrades to a plain centred cover rather than a broken pan.
        sc.style.left = "";
        sc.style.right = "";
        dom.hotspots.style.left = "";
        dom.hotspots.style.right = "";
        if (!animating) {
          sc.style.setProperty("--ws-panx", "0px");
          dom.hotspots.style.setProperty("--ws-panx", "0px");
        }
        return;
      }
      var half = overflow / 2;
      // BW-309 step 6g4: dom.hotspots gets the SAME stretch as sc, not
      // just the same translateX - the plan's own section 1 always called
      // for both elements to be stretched, so coverRect() (layoutHotspots,
      // which measures dom.hotspots' box AFTER this call) degenerates to
      // the identity and every hotspot's image-space rect maps straight
      // to a 0-100% position with no residual "natural, unstretched
      // frame" percentage math. Only sc was ever actually stretched -
      // found live by the tester: a hotspot's own containment math (which
      // already reasoned in true image-space fractions) could correctly
      // pick a rest, while the ANCHOR's rendered position - built off the
      // never-stretched box - registered somewhere else entirely, in one
      // case entirely off-canvas after a snap.
      sc.style.left = (-half) + "px";
      sc.style.right = (-half) + "px";
      dom.hotspots.style.left = (-half) + "px";
      dom.hotspots.style.right = (-half) + "px";
      if (animating) return;
      var f = PAN_F[id] != null ? PAN_F[id] : 0.5;
      var panX = (0.5 - f) * overflow;
      sc.style.setProperty("--ws-panx", panX + "px");
      dom.hotspots.style.setProperty("--ws-panx", panX + "px");
    }

    // Positions the current scene's hotspot buttons against the active art.
    // Re-entrant: called again once the art's natural size has loaded (first
    // paint of a scene, since a `new Image()` fetch races the CSS background)
    // and on every resize, so a live drag tracks the crop continuously rather
    // than only correcting on the next load.
    function layoutHotspots(scene) {
      // BW-309: must run before the box measurement below - when active it
      // stretches dom.hotspots' own box to the wide image's displayed
      // width, which is what makes coverRect() degenerate to the identity
      // for this scene. Covers every existing call site (the resize
      // requestAnimationFrame fast path and the BW-168 transitionend
      // listener included) with no further wiring.
      layoutPan(scene);
      var defs = dom.hotspotDefs || [];
      if (!defs.length) return;
      var portrait = usesPortraitArt(scene.id);
      var url = (portrait && scene.artPortrait) || scene.art;
      var box = dom.hotspots.getBoundingClientRect();
      if (!url || !box.width || !box.height) return;
      var dims = IMG_DIMS[url];
      if (!dims) { loadImageDims(url, function () { layoutHotspots(scene); }); return; }
      Array.prototype.forEach.call(dom.hotspots.children, function (elm, i) {
        var h = defs[i];
        if (!h) return;
        var rect, quad;
        if (portrait && h.rectPortrait) {
          rect = h.rectPortrait;
          quad = h.quadPortrait;
        } else if (portrait && h.strictFrame) {
          // A published hotspot has no rect calibrated for this frame - hide
          // it (also removes it from tab order) rather than laying its wide
          // rect over the portrait art. Structural hotspots keep falling
          // back to `rect` below, same as before BW-135.
          elm.style.display = "none";
          return;
        } else if (!portrait && !h.rect && h.strictFrame) {
          // Mirror of the branch above: a published hotspot with no `rect`
          // for the wide frame (the Composer published only a portrait
          // capture for this id, see publishedFor() above) must hide on wide
          // rather than fall into the `else` below with `rect` left
          // undefined - coverRect() then read rect[0] of undefined and threw,
          // aborting layoutHotspots() partway through the array so every
          // hotspot after the crashing one in `defs` order never got
          // positioned either. Structural (non-strictFrame) hotspots always
          // carry both rects, so they are unaffected.
          elm.style.display = "none";
          return;
        } else {
          rect = h.rect;
          quad = h.quad;
        }
        elm.style.display = "";
        elm.classList.add("is-placed");
        var r = coverRect(rect, dims.w, dims.h, box.width, box.height);
        elm.style.left = (r.x * 100) + "%";
        elm.style.top = (r.y * 100) + "%";
        elm.style.width = (r.w * 100) + "%";
        elm.style.height = (r.h * 100) + "%";
        // BW-165: the quad clip-path is frame-specific (a
        // portrait capture can carry its own `quadPortrait`, distinct from
        // `quad`, and the two rects need not share an aspect ratio) so it is
        // rebuilt here alongside the anchor's position on every layout pass,
        // not baked once in renderHotspots() - a stale wide-frame clip-path
        // reused against a portrait-shaped box was exactly the bug this
        // fixes: it distorted into something close to the anchor's
        // own rounded bounding box instead of tracing the drawn polygon.
        var poly = elm.querySelector(".ws-hotpoly");
        if (poly) poly.style.clipPath = quad ? quadClipPath(quad, rect) : "none";
      });
    }

    // Shelf signs (BW-109, baked-art pass) sit only in the Long Room, over
    // the brass signs baked straight into scene-store-wide-5.jpg /
    // -portrait-5.jpg (tools/bake_scene_labels_v5.py) - this file only
    // positions the invisible CLICK AREA over each one. The <a> tags are
    // real Liquid, grafted into root by build() above, never rebuilt by JS
    // - this maps each one's fixed SHELF_SLOTS rect through the same
    // image-space cover transform as layoutHotspots(), and hides a click
    // area whose slot has no anchor for the frame currently on screen
    // (mount/tilt in SHELF_SLOTS are read only by tools/bake_scene_signs.py
    // now, at bake time - nothing here needs them any more).
    function layoutPlaques() {
      if (!dom.plaques) return;
      var scene = graph.scenes[store.get().scene];
      // BW-139: once the Composer has published hotspots for this scene, its
      // shelf_plaque anchors are retired outright rather than left to occlude
      // the published links they duplicate (or, going forward, diverge from).
      var showPlaques = !!scene && scene.id === "longroom" && !scenePublished(scene.id);
      // .ws-shelfmarks is display:none by default in CSS (mobile guard +
      // no-flash-before-JS), so hiding it back needs the same "none" value -
      // an empty string would only clear the inline override and fall
      // straight back to that CSS default instead of showing anything.
      dom.plaques.style.display = showPlaques ? "block" : "none";
      if (!showPlaques) return;
      var portrait = usesPortraitArt(scene.id);
      var url = (portrait && scene.artPortrait) || scene.art;
      var box = dom.hotspots.getBoundingClientRect();
      if (!url || !box.width || !box.height) return;
      var dims = IMG_DIMS[url];
      if (!dims) { loadImageDims(url, layoutPlaques); return; }
      Array.prototype.forEach.call(dom.plaques.querySelectorAll(".ws-shelfmark"), function (elm) {
        var slot = SHELF_SLOTS[elm.dataset.slot];
        var rect = slot && (portrait ? slot.rectPortrait : slot.rect);
        if (!rect) { elm.style.display = "none"; return; }
        var r = coverRect(rect, dims.w, dims.h, box.width, box.height);
        elm.style.display = "";
        elm.style.left = (r.x * 100) + "%";
        elm.style.top = (r.y * 100) + "%";
        elm.style.width = (r.w * 100) + "%";
        elm.style.height = (r.h * 100) + "%";
      });
    }

    // BW-141: a hotspot's quad, expressed as CSS percentages relative to its
    // OWN bbox (h.rect) - clip-path percentages are already relative to the
    // clipped element's box, so this is scale-invariant and computed once
    // here rather than on every resize (unlike layoutHotspots(), which
    // re-maps rect through the art's live cover-fit transform every time).
    function quadClipPath(quad, rect) {
      var x0 = rect[0], y0 = rect[1], w = rect[2] || 1, h = rect[3] || 1;
      var pts = quad.map(function (p) {
        var rx = ((p[0] - x0) / w) * 100;
        var ry = ((p[1] - y0) / h) * 100;
        return rx.toFixed(2) + "% " + ry.toFixed(2) + "%";
      });
      return "polygon(" + pts.join(", ") + ")";
    }

    function renderHotspots(scene, s) {
      // BW-155: the nodes an arm was tracking are about to be discarded -
      // any store update re-renders every hotspot from scratch, not just a
      // scene change, so a stale armedHotspot reference must never survive.
      disarmHotspot();
      dom.hotspots.innerHTML = "";
      dom.hotspotDefs = scene.hotspots || [];
      dom.hotspotDefs.forEach(function (h) {
        // BW-135: a published click area with a real destination URL is a
        // real <a href> - native middle-click / open-in-new-tab, no click
        // handler needed for that. A structural, hand-authored in-world move
        // (no url, e.g. the pre-BW-200 storefront door) stays a <button>,
        // since it only changes scene state. BW-200: a published `goto`
        // hotspot (a Composer-authored "walk to a scene" click area) also
        // carries a real url now, so it renders as an <a> too - a plain
        // click still walks in-world (below), but a modified click, or any
        // navigation before this script runs, uses the href for real. Both
        // element kinds are reachable by Tab as well as by mouse.
        var hasHref = h.action && h.action.url;
        var d = hasHref ? el("a", "ws-hotspot") : el("button", "ws-hotspot");
        if (hasHref) d.href = h.action.url;
        else d.type = "button";
        // BW-305: presentation mode. "hover" adds no class - hover-mode
        // markup stays byte-for-byte what it was before this ticket.
        if (h.presentation === "pulse" || h.presentation === "outline") d.className += " ws-hotspot--" + h.presentation;
        d.setAttribute("aria-label", h.label);
        // BW-141: a schema-2 published hotspot carries its drawn quad - clip
        // the actual hit area (and the visible ring, via CSS) to that
        // polygon rather than the anchor's rectangular bbox. The anchor
        // itself keeps being positioned by `rect` exactly as before; only
        // pointer targeting and the hover/focus ring move onto this child.
        // No quad on either frame (schema 1, or a structural hotspot) ->
        // renders exactly as today. The clip-path itself is left unset here
        // and filled in by layoutHotspots() immediately after this render
        // (see BW-165 there) because a portrait capture can
        // carry its own `quadPortrait`/`rectPortrait`, distinct from
        // `quad`/`rect` - baking the wide quad in unconditionally here
        // previously left every portrait-only hotspot (no `quad`/`rect` at
        // all, e.g. "Cooking") with no polygon whatsoever, falling back to
        // the plain rounded bounding-box ring on mobile.
        if ((h.quad && h.rect) || (h.quadPortrait && h.rectPortrait)) {
          d.className += " ws-hotspot--quad";
          var poly = el("span", "ws-hotpoly");
          d.appendChild(poly);
        }
        // a visible label so the area reads as clickable (no key press needed).
        // Hover is CSS-only (:hover) - we deliberately do NOT update store state
        // on hover, so hovering never rebuilds this node and the click always
        // lands on a live element and activates.
        var tag = el("span", "ws-hotlabel"); tag.textContent = h.label;
        d.appendChild(tag);
        // BW-155: touch two-tap. On a coarse-pointer device the first tap
        // only arms this area (gold ring + label chip - the same .is-armed
        // CSS the desktop :hover rule already carries) instead of
        // navigating/activating; a second tap on the SAME armed area
        // completes the action. Mouse and keyboard fall straight through
        // isTouchClick() to the original single-click behaviour below.
        d.addEventListener("pointerenter", function () { warmTarget(h); });
        d.addEventListener("focus", function () { warmTarget(h); });
        d.addEventListener("click", function (ev) {
          if (isTouchClick(ev)) {
            if (d !== armedHotspot) {
              ev.preventDefault();
              warmTarget(h);
              armHotspot(d);
              return;
            }
            disarmHotspot();
          }
          if (!hasHref) { actions.activate(h); return; }
          // Only a plain click/tap - no modifier key, primary button, so
          // middle-click, ctrl/cmd-click and shift-click still open the real
          // href in a new tab exactly as a normal <a> would - is intercepted
          // below; every other case falls straight through to real
          // navigation via the anchor's own href.
          if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
          // BW-200: a published "walk to a scene" click area (the storefront
          // door, once Composer-authored) walks in-world on a plain click,
          // same as the hand-authored structural door always has.
          if (h.action.type === "goto") {
            ev.preventDefault();
            actions.activate(h);
            return;
          }
          // BW-163/BW-166: a plain click/tap on ANY plaque pointing at a real
          // /collections/ URL walks to the in-scene shelf instead of leaving
          // the world, matching what the assistant's "take me to <genre>"
          // path already does (actions.openCollection() below) - the shelf
          // fetches its own covers from that URL (renderShelf()/
          // fetchShelf()), so it no longer matters whether this room's own
          // genre data recognises the handle. A non-collection href falls
          // straight through to real navigation.
          var href = collectionForNavUrl(h.action.url);
          if (href) {
            ev.preventDefault();
            actions.openCollection({ url: href, name: h.label }, [ROOM]);
          }
        });
        dom.hotspots.appendChild(d);
      });
      layoutHotspots(scene);   // rects are image-space; see the block above
    }

    // scene-specific in-world overlays (signs, plates, the shelf)
    // BW-166: render() is store.subscribe(render) - EVERY store.set() call
    // re-runs this, not just a scene change (mood cycling, HUD toggle, a
    // hover elsewhere...). Rebuilding the shelf's covers used to be cheap
    // (a synchronous loop over already-loaded data); now it means a network
    // fetch and a fresh (scroll-reset-to-top) .ws-shelfwrap, so an unrelated
    // update while browsing the SAME shelf must be a no-op rather than
    // silently re-fetching and resetting scroll under the shopper's thumb.
    var shownShelfUrl; // undefined = not currently on the shelf scene
    function renderOverlay(scene, s) {
      if (scene.id === "shelf" && shownShelfUrl === (s.collectionUrl || null) && dom.overlay.firstChild) {
        return;
      }
      shownShelfUrl = scene.id === "shelf" ? (s.collectionUrl || null) : undefined;
      dom.overlay.innerHTML = "";
      (scene.signs || []).forEach(function (sg) {
        var w = el("div", "ws-hsign");
        w.style.left = sg.x * 100 + "%";
        w.style.top = sg.y * 100 + "%";
        w.style.transform = "translate(-50%,-50%)";
        w.appendChild(el("div", "ws-chainrow", '<span class="ws-chain"></span><span class="ws-chain"></span>'));
        var b = el("div", "ws-signboard",
          '<div class="ws-signtxt">' + sg.text + '</div><div class="ws-signsub">' + sg.sub + "</div>");
        w.appendChild(b);
        dom.overlay.appendChild(w);
      });
      (scene.plates || []).forEach(function (p) {
        var d = el("div", "ws-plate", p.text);
        d.style.left = p.x * 100 + "%";
        d.style.top = p.y * 100 + "%";
        d.style.transform = "translate(-50%,-50%)";
        dom.overlay.appendChild(d);
      });
      if (scene.id === "shelf") renderShelf(s);
    }

    // The shelf: a vertically-scrolling bookcase. BW-166: when a real
    // collection sent us here (s.collectionUrl), the shelf is filled by
    // fetching that collection's OWN /collections/<handle> page - one
    // request via the Section Rendering API (templates/collection.json
    // names main-collection.liquid's section "main") - and adopting its
    // .ws-shelfscroll markup, the same shelf-book.liquid cards
    // buildBook()/shelf-view.liquid mirror. That keeps the shelf always
    // exactly what the real collection page would show (filters, stock,
    // pagination-aware count), with no separate covers payload baked into
    // the page and no risk of drifting from it. Covers stand cover-out on
    // the planks of the shelf tile, which repeats down the y axis for as
    // long as there are books (CSS grid's job, assets/shelf.css) - this
    // only pours the books in. Clicking a book opens its product page (the
    // anchor's href).
    var shelfFetchToken = 0;
    function renderShelf(s) {
      var wrap = el("div", "ws-shelfwrap");
      var scroll;

      if (s.collectionUrl) {
        // Opened immediately with a loading state - the wood grid (the
        // shelf tile is the div's own background, not per-row) stays
        // visible the instant the shopper arrives, before the fetch lands.
        scroll = el("div", "ws-shelfscroll ws-shelfscroll--loading");
        scroll.appendChild(el("div", "ws-shelfnote", "Finding the shelf…"));
        wrap.appendChild(scroll);
        dom.overlay.appendChild(wrap);
        dom.overlay.appendChild(el("div", "ws-shelfvign"));
        fetchShelf(s.collectionUrl, wrap, s.collectionName);
        return;
      }

      // Nothing real led here (a stray direct scene jump, no collection
      // picked) - the featured fallback, same as before BW-166.
      scroll = el("div", "ws-shelfscroll");
      FEATURED.forEach(function (bk, i) { scroll.appendChild(buildBook(bk, i)); });
      if (!FEATURED.length) {
        scroll.appendChild(el("div", "ws-shelfnote", "No books on these shelves yet"));
      }
      wrap.appendChild(scroll);
      dom.overlay.appendChild(wrap);
      dom.overlay.appendChild(el("div", "ws-shelfvign"));
    }

    // Fetches the real collection page's own main-collection.liquid section
    // (Section Rendering API - mirrors the ?section_id= + DOMParser pattern
    // predictive-search.js already uses) and swaps its rendered
    // .ws-shelfscroll into `wrap` in place of the loading grid.
    // BW-166 fix: the API takes the section's FILE name - "main-collection"
    // - not the JSON template's id for it ("main" in
    // templates/collection.json); "main" 404s (proven live). shelf-view.liquid
    // stamps that node's own filter-aware product count as data-count, which
    // decides whether the "See all <name> books" sign shows below - it does
    // when the shelf holds fewer than the full collection (this fetch is
    // always page 1, 48 products - main-collection.liquid's own cap); the
    // fetched fragment's
    // .shelf-pager is never adopted in the first place, since we only pull
    // out .ws-shelfscroll (a sibling of it, not a parent), so there is
    // nothing to strip. A later, superseded call (the shopper opened a
    // different shelf before this one landed) is dropped via the token; any
    // other failure - HTTP error, the storefront password gate redirecting
    // the request, or a response with no .ws-shelfscroll at all - falls back
    // to a real page load, exactly what a plain <a> would have done.
    function fetchShelf(url, wrap, name) {
      var token = ++shelfFetchToken;
      var sep = url.indexOf("?") === -1 ? "?" : "&";
      fetch(url + sep + "section_id=main-collection")
        .then(function (r) {
          if (!r.ok) throw new Error("shelf fetch failed: " + r.status);
          if (r.redirected && /\/password(?:[\/?]|$)/.test(r.url)) {
            throw new Error("shelf fetch hit the password gate");
          }
          return r.text();
        })
        .then(function (html) {
          if (token !== shelfFetchToken) return; // superseded - discard
          var doc = new DOMParser().parseFromString(html, "text/html");
          var fresh = doc.querySelector(".ws-shelfscroll");
          if (!fresh) throw new Error("no .ws-shelfscroll in the response");
          fresh.classList.remove("ws-shelfscroll--loading");
          var shown = fresh.querySelectorAll(".ws-book").length;
          var total = parseInt(fresh.getAttribute("data-count"), 10);
          if (isNaN(total)) total = shown;
          var needsMore = total > shown;
          if (needsMore) {
            var more = doc.createElement("a");
            more.className = "ws-shelfnote ws-shelf-more";
            more.href = url;
            more.textContent = (name ? "See all " + name + " books" : "See all books") + " →";
            fresh.appendChild(more);
          }
          // Built and mutated in the fetched (detached) document above,
          // then adopted here the same way predictive-search.js does -
          // string handoff, not cross-document node insertion. An
          // addEventListener call on the detached `more` node above would
          // be lost at this string round-trip, so the offline-preview
          // click guard below runs against the live node instead, after
          // the swap.
          wrap.innerHTML = fresh.outerHTML;
          if (needsMore && !DATA.shopify) {
            var liveMore = wrap.querySelector(".ws-shelf-more");
            if (liveMore) liveMore.addEventListener("click", function (e) { e.preventDefault(); });
          }
        })
        .catch(function () {
          if (token !== shelfFetchToken) return;
          // BW-380: replace, not href - the in-scene "walk to this shelf"
          // transition never pushed its own history entry for a plain <a>
          // navigation, so falling back to one here would leave a bogus
          // extra Back step landing on the same shelf that just failed.
          window.location.replace(url);
        });
    }

    // A single cover-out book: cover image + brass plaque. An <a> when the book
    // has a product URL (click = go to the product page), else an inert button.
    // image/imageSet/sizes mirror shelf-book.liquid's <img> exactly - the URLs
    // are computed in Liquid (walkable-product.liquid), never here.
    function buildBook(bk, i) {
      var book = el(bk.url ? "a" : "button", "ws-book");
      if (bk.url) book.href = bk.url;
      // BW-222: compareAt comes straight off walkable-product.liquid's JSON
      // (product.compare_at_price) - null when the book isn't on sale.
      var onSale = bk.compareAt != null && bk.compareAt > bk.price;
      var cover = el("span", "ws-book-cover");
      if (onSale) {
        var badge = el("span", "ws-badge-sale");
        var badgeWord = el("span");
        badgeWord.setAttribute("aria-hidden", "true");
        badgeWord.textContent = "Sale";
        var badgeSr = el("span", "visually-hidden");
        badgeSr.textContent = "On sale";
        badge.appendChild(badgeWord);
        badge.appendChild(badgeSr);
        cover.appendChild(badge);
      }
      if (bk.image) {
        var img = el("img");
        img.src = bk.image; img.alt = bk.title || ""; img.loading = "lazy";
        if (bk.imageSet) img.srcset = bk.imageSet;
        img.sizes = "(max-width: 760px) 92px, 116px";
        img.width = 232; img.height = 348;
        cover.appendChild(img);
      } else {
        // Palette lives in CSS (.ws-ph-0..11, shelf.css) - mirrors shelf-book.liquid,
        // which emits the same class from forloop.index0 modulo 12.
        cover.classList.add("ws-ph-" + (i % 12));
        var ph = el("span", "ws-book-ph"); ph.textContent = bk.title || "";
        cover.appendChild(ph);
      }
      var plaque = el("span", "ws-plaque");
      var pt = el("span", "ws-plaque-t"); pt.textContent = bk.title || "Untitled";
      plaque.appendChild(pt);
      if (bk.author) { var pa = el("span", "ws-plaque-a"); pa.textContent = bk.author; plaque.appendChild(pa); }
      if (onSale) {
        var priceRow = el("span", "ws-plaque-price");
        var was = el("span", "ws-plaque-was");
        var wasSr = el("span", "visually-hidden"); wasSr.textContent = "Was";
        was.appendChild(wasSr);
        was.appendChild(document.createTextNode(money(bk.compareAt) || ""));
        var now = el("span", "ws-plaque-now");
        var nowSr = el("span", "visually-hidden"); nowSr.textContent = "Now";
        now.appendChild(nowSr);
        now.appendChild(document.createTextNode((bk.priceFrom ? "from " : "") + (money(bk.price) || "")));
        priceRow.appendChild(was);
        priceRow.appendChild(now);
        plaque.appendChild(priceRow);
      } else {
        var pp = el("span", "ws-plaque-p"); pp.textContent = (bk.priceFrom ? "from " : "") + (money(bk.price) || "");
        plaque.appendChild(pp);
      }
      book.appendChild(cover);
      book.appendChild(plaque);
      if (!bk.url) book.addEventListener("click", function (ev) { ev.preventDefault(); });
      return book;
    }

    // The Browse panel is a real navigation surface, not an in-world walk:
    // every leaf (a genre with no subs, or a sub-genre) is a plain <a href>
    // straight to its /collections/<handle> shelf (BW-117's AC), matching
    // "Shop all"/leaf links elsewhere in this file - only genre-with-subs
    // rows are buttons, and only to reveal the sub column in place. In
    // "mobile" mode every genre row is a real link regardless of subs (no
    // third column on the tab bar's picker - sub-genre refinement moves to
    // the facet rail once on a shelf).
    // BW-258: this scene's own genre objects ({handle, title, count, url,
    // subs: [{title, count, url}]}) reshaped to the shared drawer's node
    // shape (assets/genre-drawer.js) - `key` is what the drawer matches
    // s.browseGenre against.
    function toDrawerGenres(list) {
      return (list || []).map(function (g) {
        return {
          key: g.handle, title: g.title, count: g.count, url: g.url,
          subs: (g.subs || []).map(function (sub) {
            return { title: sub.title, count: sub.count, url: sub.url };
          })
        };
      });
    }

    function renderBrowsePanel(s) {
      var b = dom.browse;
      if (!b) return;
      var browsePill = dom.beam.querySelector(".ws-beam-browse");
      if (browsePill) browsePill.setAttribute("aria-expanded", s.browseOpen ? "true" : "false");

      window.BookwormsGenreDrawer.render(b,
        { open: s.browseOpen, mobile: s.browseMode === "mobile", cat: s.browseCat, genre: s.browseGenre },
        {
          mode: "nav",
          categories: [
            { key: "fiction", genres: toDrawerGenres(graph.fiction) },
            { key: "nonfiction", genres: toDrawerGenres(graph.nonfiction) }
          ],
          // pickBrowseGenre() re-renders, which wipes and rebuilds the genre
          // column - genre-drawer.js's own click handler already stops
          // propagation before calling this, so the document click-outside
          // listener never sees a click on a now-parentless node and closes
          // the whole panel instead of opening the sub column.
          onGenre: function (key) { actions.pickBrowseGenre(key); },
          onLeafClick: DATA.shopify ? null : function (ev) { ev.preventDefault(); }
        }
      );
    }

    // A live drag-resize needs hotspots tracking the crop continuously, not
    // only correcting once the heavier debounced re-render (start(), below)
    // catches up 150ms after the pointer stops - this is that fast path,
    // just the image-anchored layout, on every frame the browser offers.
    //
    // BW-309 step 6g2: a mobile browser's address-bar/toolbar collapsing
    // or expanding during a page scroll fires a BURST of resize events
    // over 1-4s, each changing only root.clientHeight (the frame's own
    // height is 100dvh, BW-99, which re-measures live as that chrome
    // slides away) - not root.clientWidth. A wide-swipe scene's stretch
    // and --ws-panx only ever depend on width via layoutPan()'s own
    // overflow math (a real resize or an orientation change, both of
    // which always change width too), so this rAF-throttled fast path
    // used to still re-run layoutHotspots -> layoutPan on nearly every
    // frame of that animation regardless, each one computing a slightly
    // different pixel value from the ticking height - a re-layout storm,
    // found live: Adam's "screen judders for 1-4 seconds" scrolling to
    // the footer. lastFastResizeWidth is compared with a small tolerance,
    // not strict equality - getBoundingClientRect-derived widths can read
    // a fraction of a pixel either side of the true value on a height-only
    // change alone (device-pixel-ratio rounding). This gate sits at the
    // RESIZE EVENT ENTRY POINT, not inside layoutPan() itself, so a scene
    // entry, a drag, a keyboard press, or the peek - none of them resize
    // events - always still get a full, fresh, correct layout.
    // BW-309 step 6g5: the width gate above stops layoutPan() re-deriving
    // anything DURING a height-only change, but something else can still
    // sneak in ONE stale re-layout mid-storm and then never correct it -
    // found live (real CDP touch, tester): --ws-beam-h is `clamp(58px, 9vh,
    // 92px)`, a genuinely height-dependent CSS value, so the has-beam `top`
    // transition on .ws-scenes/.ws-hotspots fires for real on a height
    // step, and the pre-existing BW-168 transitionend hook (filtered to
    // propertyName "top", nothing to do with THIS ticket's width gate)
    // re-runs layoutHotspots()/layoutPlaques() straight from that listener,
    // bypassing the gate entirely - once, against whatever height was
    // current at that moment, then never again once the CSS transition
    // stops firing new transitionend events for the rest of the cycle.
    // The scene is left stretched against a STALE mid-cycle height for
    // good, even once the viewport returns to its original size, since
    // nothing else re-triggers without a genuine width change.
    //
    // Fix: a trailing settle timer, independent of the width gate. Every
    // height-only resize event (re)starts a 300ms timer; once the height
    // has been quiet that long, run ONE full layoutHotspots -> layoutPan
    // pass against whatever the box measures NOW (deferred one rAF so the
    // browser has actually painted the settled height first). layoutPan()
    // re-derives --ws-panx from PAN_F, same f as before - only the
    // stretch/band/top can change, never the rest. If a gesture or the
    // peek owns the coordinate system when the timer fires, retry on the
    // same 300ms cadence rather than silently no-op'ing via layoutPan()'s
    // own internal animating guard - a trailing settle that never lands is
    // exactly the bug this step fixes.
    var layoutRaf = null;
    var lastFastResizeWidth = root.clientWidth;
    var settleTimer = null;
    function runTrailingSettle() {
      settleTimer = null;
      requestAnimationFrame(function () {
        var scene = graph.scenes[store.get().scene];
        if (!scene) return;
        var sc = dom.scenes.querySelector('[data-scene="' + scene.id + '"]');
        var animating = sc && (sc.classList.contains("is-panning")
          || sc.classList.contains("is-snapping")
          || sc.classList.contains("is-peeking"));
        if (animating) { settleTimer = setTimeout(runTrailingSettle, 300); return; }
        lastFastResizeWidth = root.clientWidth;
        layoutHotspots(scene);
        layoutPlaques();
      });
    }
    window.addEventListener("resize", function () {
      var widthChanged = Math.abs(root.clientWidth - lastFastResizeWidth) >= 0.5;
      if (widthChanged) {
        clearTimeout(settleTimer);
        settleTimer = null;
        if (layoutRaf) return;
        layoutRaf = requestAnimationFrame(function () {
          layoutRaf = null;
          lastFastResizeWidth = root.clientWidth;
          var scene = graph.scenes[store.get().scene];
          // BW-309 step 6h2: the other of the two triggers allowed to
          // re-derive docTop (see fitFrameHeight()'s own comment) - a real
          // width change (includes rotation), never the height-only path
          // below.
          if (scene) fitFrameHeight(scene, wideSwipeActive(scene.id));
          if (scene) layoutHotspots(scene);
          layoutPlaques();
        });
        return;
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(runTrailingSettle, 300);
    });

    store.subscribe(render);
    store.subscribe(pushView);   // BW-380: runs after render(), see pushView()'s own comment
    render(store.get());

    // BW-380 boot: give this page load a ws-tagged history.state so
    // pushView()/popstate have something real to compare against from the
    // very first transition, and clean up the contact form's own redirect
    // litter (BW-97/BW-364's {% form 'contact' %} redirects back to this
    // same page on submit as ?contact_posted=true&form_type=contact#contact_form).
    // The confirmation text really did render - posted_successfully? is
    // baked straight into the book's own content - but the book itself was
    // never reopened after the reload, so submitting looked like nothing
    // had happened.
    var bootPathname = window.location.pathname;   // captured before openBook() below can touch it
    var bootParams = null;
    try { bootParams = new URLSearchParams(window.location.search); } catch (e) {}
    // applying suppresses openBook()'s own pushView() call - boot gets exactly
    // one history write (the replaceState below), never a push.
    applying = true;
    if (bootParams && bootParams.get("contact_posted") === "true") openBook("contact", null);
    applying = false;
    var bootState = stateOf();
    var bootUrl = bootPathname;
    if (bootParams) {
      bootParams.delete("contact_posted");
      bootParams.delete("form_type");
      var bootQs = bootParams.toString();
      bootUrl = bootPathname + (bootQs ? "?" + bootQs : "");
    }
    try { window.history.replaceState(bootState, "", bootUrl); } catch (e) {}

    window.addEventListener("popstate", function (ev) {
      // Same guard as the Escape handler in start() - the product modal
      // owns Back while it is open (pdp-modal.js's own popstate listener
      // closes it), so this listener does nothing on that first Back and
      // only restores the underlying view on the second.
      if (document.querySelector("dialog.pdp-modal[open]")) return;
      applying = true;
      applyState(ev.state || bootState);
      applying = false;
    });

    // bfcache restore (e.g. Safari's swipe-back-then-forward): the page's
    // own JS state never re-ran, but the DOM/store could be stale against
    // whatever history.state now says - re-apply it the same way a popstate
    // would.
    window.addEventListener("pageshow", function (ev) {
      if (!ev.persisted) return;
      applying = true;
      applyState(window.history.state || bootState);
      applying = false;
    });

    return { render: render };
  }

  // -------------------------------------------------------------- Experience
  function start() {
    var host = document.getElementById("walkable-store");
    if (!host) return;
    var graph = buildSceneGraph();

    // A shelf page's "&larr; The Long Room" crumb links back here with
    // ?scene=longroom so the shopper lands in-world rather than at the
    // default start scene. Only honoured when it names a scene that
    // actually exists - a stale/foreign query param falls through to the
    // usual default. No hash routing, no history writes: this is read once
    // on load.
    var requestedScene = null;
    try {
      requestedScene = new URLSearchParams(window.location.search).get("scene");
    } catch (e) { /* no query string support - fall through to the default */ }
    var startScene = (requestedScene && graph.scenes[requestedScene])
      ? requestedScene
      : (DATA.startScene || "storefront");

    var initialMood = DATA.mood || "overcast";
    if (initialMood !== "overcast") host.className = "ws-stage mood-" + initialMood;
    var store = createStore({
      scene: startScene, prevScene: null, forward: true,
      focus: null,
      browseOpen: false, browseMode: "full", browseCat: "fiction", browseGenre: null,
      cartCount: DATA.cartCount || 0,
      mood: initialMood, showHud: DATA.showHud !== false,
      collectionHandle: null, collectionName: null,
      collectionUrl: null, collectionCount: 0, trail: []
    });
    var camera = createCamera(graph, store);

    // BW-213/BW-218: every add-to-cart path in the theme (this scene's own
    // actions.addToCart below, the modal product view's assets/pdp-modal.js,
    // and a real product page's own form via assets/cart-drawer.js) reads
    // its authoritative item_count off the freshly rendered cart-drawer
    // section (never a /cart.js round trip) and dispatches this, rather than
    // any of them reaching into this closure directly - render()'s existing
    // s.cartCount wiring (.ws-cart/.ws-beam-bag ".n") picks the new count up
    // on the next render with no further wiring.
    document.addEventListener("bw:cart-updated", function (ev) {
      if (ev.detail && typeof ev.detail.itemCount === "number") {
        store.set({ cartCount: ev.detail.itemCount });
      }
    });

    var MOODS = ["overcast", "evening", "golden"];

    // Every scene you can walk into gets a trail back to the hub. The street and
    // the room ARE the hub's two faces, so neither hangs a sign.
    function trailToScene(id) {
      if (id === "longroom" || id === "storefront") return [];
      return [ROOM, { label: graph.scenes[id].name }];
    }

    var actions = {
      focus: function (h) { store.set({ focus: h }); },
      blur: function () { store.set({ focus: null }); },
      activate: function (h) { runAction(h.action, h); },
      primary: function () {
        var scene = graph.scenes[store.get().scene];
        var h = forwardHotspot(scene);
        if (h) runAction(h.action, h);
      },
      // The Browse panel (BW-117): `mode` is "full" (desktop, beam pill,
      // Category -> Genre -> Sub-genre) or "mobile" (Shelves tab,
      // Category -> Genre only). Toggling closed if it's already open in
      // the same mode; switching mode re-opens fresh rather than carrying
      // over a drilled-in genre from the other surface.
      toggleBrowsePanel: function (mode) {
        var st = store.get();
        if (st.browseOpen && st.browseMode === mode) {
          store.set({ browseOpen: false });
        } else {
          store.set({ browseOpen: true, browseMode: mode, browseCat: "fiction", browseGenre: null });
        }
      },
      closeBrowsePanel: function () { store.set({ browseOpen: false }); },
      setBrowseCat: function (cat) { store.set({ browseCat: cat, browseGenre: null }); },
      pickBrowseGenre: function (handle) { store.set({ browseGenre: handle }); },
      cycleMood: function () {
        var i = (MOODS.indexOf(store.get().mood) + 1) % MOODS.length;
        var m = MOODS[i];
        host.className = "ws-stage mood-" + m;
        store.set({ mood: m });
      },
      // Walk to a shelf. `trail` is the path that got you here; the collection
      // itself becomes its last step, which is what the hanging sign reads out.
      openCollection: function (c, trail) {
        var name = c.name || c.title || null;
        store.set({
          collectionHandle: c.handle || null,
          collectionName: name,
          collectionUrl: c.url || null,
          collectionCount: c.count || 0
        });
        camera.goTo("shelf", {
          forward: true,
          trail: (trail || [ROOM]).concat([{ label: name || "The shelves" }])
        });
      },
      // Step back along the trail: return to the scene carrying the path that
      // reaches it, so you land where you left. The hub hangs no sign,
      // whatever led here.
      walkTo: function (step, back) {
        camera.goTo(step.scene, {
          forward: false,
          trail: trailToScene(step.scene).length ? back : []
        });
      },
      // A tab-bar or hotkey jump: no path led here, so the trail is just the way
      // home. Returning to the hub reads as walking back, not walking in.
      goToScene: function (id) {
        camera.goTo(id, { forward: id !== "longroom", trail: trailToScene(id) });
      },
      // BW-218: dispatches bw:cart-updated (with the cart-drawer section's
      // own fresh HTML) instead of just bumping cartCount locally, so the
      // badge and the added-to-bag drawer always agree - both this scene's
      // own bw:cart-updated listener above and assets/cart-drawer.js react
      // to the one event. Demo mode (no variantId, or DATA.shopify false -
      // no real Shopify cart to add to) keeps the old optimistic local bump,
      // since there is nothing to fetch and no drawer content to show.
      addToCart: function (bk) {
        if (bk && bk.variantId && DATA.shopify) {
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: bk.variantId, quantity: 1, sections: "cart-drawer" })
          })
            .then(function (r) {
              if (!r.ok) throw new Error("add failed");
              return r.json();
            })
            .then(function (cart) {
              var sections = cart.sections || {};
              var html = sections["cart-drawer"];
              var itemCount = window.BookwormsCart ? window.BookwormsCart.itemCountFromHtml(html) : null;
              if (itemCount === null) itemCount = store.get().cartCount + 1;
              document.dispatchEvent(new CustomEvent("bw:cart-updated", { detail: { itemCount: itemCount, sections: sections } }));
            })
            .catch(function () {});
        } else {
          store.set({ cartCount: store.get().cartCount + 1 });
        }
      },
      openCart: function () {
        if (DATA.shopify) window.location.href = "/cart";
      },
      // The mobile Search tab and the beam pill (buildBeam()) both leave the
      // world for the real /search page - there is no in-world search scene.
      openSearch: function () {
        if (DATA.shopify) window.location.href = SITE.searchUrl || "/search";
      }
    };

    function runAction(a, h) {
      if (!a) return;
      if (a.type === "goto") {
        // BW-200: a scene id can drift out of the graph (the WALK_TARGETS
        // list on the backend disagreeing with this file's own scene ids -
        // guarded server-side by a test, but that test skips when this file
        // is absent from a backend-only checkout). Degrade to a real
        // navigation on the hotspot's own url rather than silently doing
        // nothing.
        if (graph.scenes[a.scene]) camera.goTo(a.scene, { forward: a.forward, trail: trailToScene(a.scene) });
        else if (a.url) window.location.href = a.url;
      }
      else if (a.type === "nav" && DATA.shopify) window.location.href = a.url;
      else if (a.type === "openCollection") actions.openCollection(h);
    }

    Renderer2D(host.querySelector(".ws-frame") || createFrame(host), graph, store, camera, actions);

    // A shop is not a game: nothing here is driven by a letter. Every way through
    // the world is a labelled thing you click (door, aisle, genre row, assistant),
    // and each is a <button>, so Tab and Enter already walk it.
    //
    // Escape is the single exception, because "get me out of here" needs to work
    // without hunting for the way back. It backs out of whatever is innermost:
    // a picked book, then the sub-genre column, then the wall board, then the
    // scene itself.
    //
    // Letters were bound to the camera (E, Q, M, F) and that was a bug in
    // disguise: typing "something" to the assistant walked the shopper off
    // mid-word, once per matching letter.
    function isTyping(ev) {
      var t = ev.target;
      return !!t && (t.isContentEditable ||
        /^(input|textarea|select)$/i.test(t.tagName || ""));
    }

    document.addEventListener("keydown", function (ev) {
      if (ev.key !== "Escape" || isTyping(ev)) return;
      // Escape belongs to whatever is on top. The page book closes on it, and so
      // does the assistant; without yielding, closing either would ALSO walk the
      // shopper into the Long Room from wherever they were standing.
      if (document.querySelector(".ws-tome-ov")) return;
      if (document.querySelector(".ws-assist.is-open")) return;
      // BW-213: the modal product view owns Escape while it is open (see
      // assets/pdp-modal.js's own 'cancel' handler) - otherwise closing it
      // would ALSO walk the shopper into the Long Room underneath it.
      if (document.querySelector("dialog.pdp-modal[open]")) return;

      var s = store.get();
      if (s.browseGenre) store.set({ browseGenre: null });
      else if (s.browseOpen) store.set({ browseOpen: false });
      else actions.goToScene("longroom");
    });

    // Click outside the Browse panel (or its opening pill/tab) closes it -
    // same convention as assets/predictive-search.js's dropdown.
    document.addEventListener("click", function (ev) {
      if (!store.get().browseOpen) return;
      if (ev.target.closest(".ws-browse, .ws-beam-browse, .ws-mtab")) return;
      store.set({ browseOpen: false });
    });

    // re-render on resize so a rotated phone swaps to its portrait scene
    // art. BW-309 step 6g2: same width-only gate as the fast rAF path in
    // Renderer2D (a sibling scope, so its own tracker can't be shared
    // here) - a mobile address-bar cycle fires a burst of resize events
    // over 1-4s that change only height, and this debounced handler's own
    // store.set({}) -> render() -> applySceneArt() -> layoutHotspots()
    // cascade used to still fire once the burst settled, for no reason a
    // height-only change needs it to. A real width/orientation change
    // (the actual "rotated phone" case this handler exists for) still
    // goes through untouched.
    var rz;
    var lastDebouncedResizeWidth = (document.querySelector(".ws-frame") || {}).clientWidth;
    window.addEventListener("resize", function () {
      var frame = document.querySelector(".ws-frame");
      var w = frame ? frame.clientWidth : lastDebouncedResizeWidth;
      if (Math.abs(w - lastDebouncedResizeWidth) < 0.5) return;
      clearTimeout(rz);
      rz = setTimeout(function () {
        lastDebouncedResizeWidth = w;
        store.set({});
      }, 150);
    });
  }

  function createFrame(host) {
    var f = el("div", "ws-frame");
    host.appendChild(f);
    return f;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
